/**
 * Procedural surface families — the actual "art".
 * owner: materials
 *
 * Each entry defines void surface(vec2 uv, inout Surf s) and is compiled into its
 * own fullscreen program (so no dead branches). Materials.js drives them with
 * uP[0..5] (vec4 params), uC[0..3] (linear colours) and uSeed.
 *
 * Conventions
 *   s.albedo  linear-light base colour (pass B encodes to sRGB)
 *   s.rough   perceptual roughness
 *   s.metal   0 for every dielectric. Rust, paint, grime and dirt are dielectrics:
 *             wherever they cover metal, metalness MUST fall to 0 or the surface
 *             reads as coloured chrome.
 *   s.ao      baked cavity occlusion, multiplied by the height-derived AO in pass B
 *   s.height  0..1, drives the normal map and POM
 *   s.alpha   cutout coverage (foliage)
 */

export const SURFACE_COMMON = /* glsl */`
struct Surf { vec3 albedo; float rough; float metal; float ao; float height; float alpha; };

uniform vec4  uP[6];
uniform vec3  uC[4];
uniform float uSeed;

/* rectangular brick/tile lattice. returns local uv + cell id + border distance */
struct Lat { vec2 luv; vec2 cell; float id; float border; };
Lat lattice(vec2 uv, vec2 counts, float stagger, float aspectFix){
  float rowF = uv.y * counts.y;
  float row  = floor(rowF);
  float ry   = rowF - row;
  float colF = uv.x * counts.x + mod(row, 2.0) * stagger;
  float col  = floor(colF);
  float rx   = colF - col;
  Lat l;
  l.luv  = vec2(rx, ry);
  l.cell = vec2(mod(col, counts.x), row);
  l.id   = h21(l.cell + uSeed * 0.137);
  float bx = min(rx, 1.0 - rx) * aspectFix;
  float by = min(ry, 1.0 - ry);
  l.border = min(bx, by);
  return l;
}

/* soft directional streak mask running down -Y, seeded per column */
float streaks(vec2 uv, float freq, float sharp, float seed){
  float n = tfbm2(uv, vec2(freq, freq * 0.06), 5, 0.55, seed);
  return sat(n * sharp + 0.5);
}

/* thin dark crack network from ridged noise */
float crackNet(vec2 uv, vec2 freq, float thin, float seed){
  float r = tridge(uv, freq, 6, 0.55, seed);
  return ss(thin, 1.0, r);
}
`;

export const SURFACES = {

/* ===================================================================== *
 * CONCRETE  — floors, walls, painted walls, plaster, stucco
 * ===================================================================== */
concrete: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float aggAmt = uP[0].x, crackAmt = uP[0].y, stainAmt = uP[0].z, paintAmt = uP[0].w;
  float rBase  = uP[1].x, rVar = uP[1].y, tieAmt = uP[1].z, spallAmt = uP[1].w;
  float macroF = uP[2].x, tintVar = uP[2].y, seamAmt = uP[2].z, wear = uP[2].w;
  float bubbles = uP[3].x, grit = uP[3].y, stuccoAmt = uP[3].z, edgeChip = uP[3].w;
  float sd = uSeed;

  /* A NORMAL MAP IS A DERIVATIVE, SO IT IS A HIGH-PASS FILTER ON THE HEIGHT FIELD.
   *
   * The rule at the top of this file — "every generator frequency is bounded so the
   * highest octave still gets >= ~4 texels per cycle" — was being broken by the height
   * field of this very generator, and the consequence is the interior ceiling and floor
   * reading as television static. Dumped and measured, the generated maps of
   * concrete_floor at 2048:
   *
   *     albedo   per-texel |delta| 5.05
   *     ORM      per-texel |delta| 2.67
   *     normal   per-texel |delta| 18.08     <- a field of 1-2 texel confetti
   *
   * A 2-texel height band with amplitude 0.004 produces a LARGER normal deviation than a
   * 20-texel band with amplitude 0.04, because the gradient scales with amplitude over
   * period. So the bands that were invisible in the height map dominated the normal map,
   * and the normal map is what the shading actually samples. Three offenders, all fixed
   * by frequency rather than by amplitude, so the relief that survives is real relief:
   *
   *   micro  240 x 3 octaves = 960 cycles = 2.1 texels   ->  110 x 2 = 220 = 9.3 texels
   *   fine    70 x 4 octaves = 560 cycles = 3.7 texels   ->   70 x 3 = 280 = 7.3 texels
   *   grit   420 x 2 octaves = 840 cycles = 2.4 texels   ->  140 x 2 = 280 = 7.3 texels
   *
   * (Earlier ablation had cleared the normal map wrongly: m.normalScale.set(0,0) does
   * nothing on a SURF_TRIPLANAR material, because the triplanar branch takes the shading
   * normal from gWNRM and never touches normalScale. A null result from a probe that did
   * not run is exactly the F14 trap, and it cost this diagnosis two passes.) */
  float macro = tfbm2(uv, vec2(macroF), 5, 0.58, sd);
  float mid   = tfbm2(uv, vec2(macroF * 5.0), 5, 0.5, sd + 11.0);
  float fine  = tfbm2(uv, vec2(70.0), 3, 0.5, sd + 23.0);
  float micro = tfbm2(uv, vec2(110.0), 2, 0.5, sd + 31.0);

  /* tonal blotching: real concrete is patchy, and the patches are mostly DARKER
     than the base (damp, dust, differential curing) rather than brighter */
  float blot = macro * 0.5 + 0.5;
  vec3  alb   = uC[0] * (1.0 + macro * 0.16 * tintVar + mid * 0.10 * tintVar + fine * 0.05);
  alb *= 1.0 - ss(0.55, 0.05, blot) * 0.26 * tintVar;
  float h     = 0.60 + macro * 0.012 + mid * 0.014 + fine * 0.010 + micro * 0.004;
  float rough = rBase + mid * 0.06 * rVar + fine * 0.05 * rVar;
  float ao    = 1.0;

  /* ---- stucco / render: coarse trowel texture -------------------------- */
  if (stuccoAmt > 0.001){
    float st = tturb(uv, vec2(38.0), 4, 0.55, sd + 71.0);
    float sw = tfbm2(uv, vec2(9.0, 3.0), 3, 0.5, sd + 73.0);
    h    += (st - 0.5) * 0.10 * stuccoAmt + sw * 0.03 * stuccoAmt;
    alb  *= 1.0 + (st - 0.5) * 0.16 * stuccoAmt;
    rough += (st - 0.5) * 0.06 * stuccoAmt;
  }

  /* ---- aggregate: fine sand + exposed pebbles --------------------------
   *
   * Three things were wrong and the reviewer named all three from the pixels:
   * "every aggregate stone is the same rounded blob at a different scale, all the same
   * pale albedo, and the aggregate is lighter than the matrix, which is backwards."
   *
   *  SHAPE   A Voronoi cell distance thresholded with smoothstep is a circle. Every
   *          stone was a circle. Warping the lookup with a two-octave field at ~4x the
   *          cell rate turns each cell boundary into a broken, angular outline — which
   *          is what crushed rock actually looks like — for one extra noise fetch.
   *  VALUE   pebCol = uC[1] * (0.65 + 0.7 * rnd) centres the stones on 1.0 x uC[1],
   *          and uC[1] is the PALE tone of the pair (0x847d70 against a 0x655f55 base).
   *          So the aggregate averaged brighter than the paste. In real concrete the
   *          coarse aggregate is granite, basalt or river gravel and the matrix is
   *          cement paste: the stones are the DARK part, with the occasional pale
   *          quartz or limestone chip as the exception, not the rule.
   *  HUE     One colour for every stone. Now each stone carries its own small warm/cool
   *          shift, which is what stops a mass of them reading as one printed pattern.
   */
  vec2 aggW = vec2(tfbm2(uv, vec2(150.0), 2, 0.5, sd + 301.0),
                   tfbm2(uv, vec2(150.0), 2, 0.5, sd + 307.0)) * 0.010;
  vec3 wA = tvor(uv, 170.0, sd + 3.0);
  vec3 wB = tvor(uv + aggW, 44.0, sd + 17.0);
  float sand   = ss(0.44, 0.04, wA.x);
  float wearM  = ss(0.30, 0.80, macro * 0.5 + 0.5) * wear;
  /* per-stone size, so the population has a distribution instead of one radius */
  float pebR   = 0.24 + 0.22 * h11(wB.z * 11.3 + sd);
  float pebble = ss(pebR, pebR * 0.26, wB.x) * step(0.52, wB.z) * (0.25 + 0.75 * wearM);

  float pk = h11(wB.z * 31.7 + sd);
  /* most stones dark (crushed rock), a minority pale (quartz / limestone) */
  vec3 pebCol = mix(uC[2] * 1.55, uC[1] * 1.02, pk * pk);
  pebCol *= 0.86 + 0.30 * h11(wB.z * 57.1 + sd);
  float pebHue = h11(wB.z * 13.7 + sd) - 0.5;
  pebCol *= vec3(1.0 + pebHue * 0.15, 1.0, 1.0 - pebHue * 0.13);
  alb   = mix(alb, pebCol, pebble * aggAmt * 0.85);
  h    += pebble * aggAmt * 0.030 - sand * aggAmt * 0.006;
  rough = mix(rough, rough - 0.14, pebble * aggAmt);
  alb  *= 1.0 - sand * aggAmt * 0.10;

  /* ---- entrained air bubbles / pinholes -------------------------------- */
  vec3 wC = tvor(uv, 92.0, sd + 47.0);
  float pitR = 0.025 + 0.085 * h11(wC.z * 53.0);
  float pit = ss(pitR, pitR * 0.12, wC.x) * step(0.78, wC.z);
  h   -= pit * bubbles * 0.055;
  ao  *= 1.0 - pit * bubbles * 0.45;
  alb *= 1.0 - pit * bubbles * 0.26;

  /* ---- gritty micro relief so it never reads as plastic ----------------
     Frequency down from 420 (2.4 texels on the top octave) to 140 (7.3). The ROUGHNESS
     term keeps its own, higher band: roughness is not differentiated when it is sampled,
     so a near-Nyquist band there costs a little mip flattening and nothing else, while
     the same band in the height field is amplified into the normal. */
  float gritN = tfbm2(uv, vec2(140.0), 2, 0.5, sd + 61.0);
  float gritR = tfbm2(uv, vec2(300.0), 2, 0.5, sd + 63.0);
  h     += gritN * 0.0060 * grit;
  rough += gritR * 0.05 * grit;

  /* ---- formwork board seams (horizontal recessed lines) ---------------- */
  if (seamAmt > 0.001){
    float rows = 4.0;
    float wob = tfbm2(uv, vec2(30.0, 1.5), 3, 0.5, sd + 5.0) * 0.016;
    float rf = uv.y * rows + wob;
    float ry = fract(rf);
    float rid = h11(floor(rf) * 3.7 + sd);
    float sw = 0.016 + rid * 0.026;
    float seam = ss(sw, 0.0, min(ry, 1.0 - ry)) * (0.45 + 0.55 * rid);
    h   -= seam * 0.055 * seamAmt;
    ao  *= 1.0 - seam * 0.45 * seamAmt;
    alb *= 1.0 - seam * 0.22 * seamAmt;
  }

  /* ---- form-tie holes (grid of plugged cones) --------------------------
     Real form ties sit on the formwork's tie pattern, so the grid is genuine —
     but a *perfect* grid at three per tile turns into a dot lattice you can read
     across a whole wall the moment the texture repeats. Jitter each hole inside
     its cell, drop two thirds of them, and vary the plug so no two rows match. */
  if (tieAmt > 0.001){
    vec2 cellF = uv * vec2(2.0, 1.6);
    vec2 cell = floor(cellF);
    float pick = h11(dot(cell, vec2(17.3, 39.7)) + sd * 0.71);
    float present = step(0.62, pick);
    vec2 jit = vec2(h11(dot(cell, vec2(5.1, 23.9)) + sd), h11(dot(cell, vec2(31.7, 7.3)) + sd + 4.0));
    vec2 g = fract(cellF) - 0.5 - (jit - 0.5) * 0.44;
    float d = length(g * vec2(1.0, 1.25));
    float rad = 0.052 + 0.030 * pick;
    float cone = ss(rad, rad * 0.16, d) * present;
    float ring = ss(rad * 1.45, rad, d) * ss(rad * 0.75, rad, d) * present;
    float plug = 0.55 + 0.45 * h11(pick * 91.3 + sd);
    h   -= cone * 0.085 * tieAmt * plug;
    ao  *= 1.0 - cone * 0.6 * tieAmt;
    alb  = mix(alb, uC[0] * (0.60 + 0.24 * plug), (cone * 0.75 + ring * 0.4) * tieAmt);
    rough += cone * 0.06 * tieAmt;
  }

  /* ---- hairline cracks (two networks, different scale) ----------------- */
  if (crackAmt > 0.001){
    float c1 = crackNet(uv, vec2(4.5), 0.90, sd + 101.0);
    float c2 = crackNet(uv, vec2(13.0), 0.945, sd + 211.0);
    float region = ss(0.35, 0.72, tfbm2(uv, vec2(2.2), 3, 0.5, sd + 301.0) * 0.5 + 0.5);
    float cr = sat(c1 * 1.0 + c2 * 0.75) * region * crackAmt;
    h   -= cr * 0.06;
    ao  *= 1.0 - cr * 0.8;
    alb *= 1.0 - cr * 0.55;
    rough += cr * 0.10;
  }

  /* ---- spalling: chunks broken out revealing raw aggregate ------------- */
  if (spallAmt > 0.001){
    vec3 wS = tvor(uv, 7.0, sd + 401.0);
    float sel = step(0.80, wS.z);
    float lump = ss(0.42, 0.16, wS.x + tfbm2(uv, vec2(20.0), 4, 0.5, sd + 402.0) * 0.10);
    float sp = lump * sel * spallAmt;
    h   -= sp * 0.07;
    ao  *= 1.0 - sp * 0.55;
    alb  = mix(alb, uC[1] * 0.80, sp * 0.75);
    rough += sp * 0.06;
  }

  /* ---- edge chipping: small broken flecks ------------------------------- */
  if (edgeChip > 0.001){
    vec3 wE = tvor(uv, 40.0, sd + 501.0);
    float chip = ss(0.11, 0.04, wE.x) * step(0.90, wE.z);
    h   -= chip * 0.030;
    alb  = mix(alb, uC[1] * 0.92, chip * edgeChip);
    ao  *= 1.0 - chip * 0.25;
  }

  /* ---- paint coat (applied before staining so stains sit on top) ------- */
  if (paintAmt > 0.001){
    float peelBase = tfbm2(uv, vec2(6.0), 5, 0.55, sd + 601.0) * 0.5 + 0.5;
    float peelEdge = tridge(uv, vec2(11.0), 4, 0.5, sd + 611.0);
    /* uP[4].x = peel amount. 0 -> intact coat, 1 -> heavily flaked */
    float peel = uP[4].x;
    float cov = mix(1.0, ss(0.30, 0.46, peelBase + peelEdge * 0.18), peel);
    float pn = tfbm2(uv, vec2(30.0), 4, 0.5, sd + 621.0);
    vec3 pcol = uC[3] * (1.0 + pn * 0.10);
    /* paint fills the fine relief and dulls the aggregate */
    alb   = mix(alb, pcol, cov * paintAmt);
    rough = mix(rough, 0.44 + pn * 0.10 + tfbm2(uv, vec2(120.0), 3, 0.5, sd + 631.0) * 0.06, cov * paintAmt);
    h     = mix(h, mix(h, 0.62 + macro * 0.02, 0.75), cov * paintAmt);
    /* lip where the paint has flaked away */
    float pe = peelBase + peelEdge * 0.18;
    float lip = ss(0.46, 0.36, pe) * ss(0.28, 0.36, pe) * peel;
    h  += lip * 0.02 * paintAmt;
    ao *= 1.0 - lip * 0.35 * paintAmt;
  }

  /* ---- water staining / soot: dark vertical runs ----------------------- */
  if (stainAmt > 0.001){
    float run = streaks(uv, 26.0, 1.4, sd + 701.0);
    float top = ss(0.05, 0.85, 1.0 - uv.y);
    float blot = ss(0.30, 0.85, tfbm2(uv, vec2(3.0), 4, 0.55, sd + 711.0) * 0.5 + 0.5);
    float st = sat(run * 0.85 + 0.10) * mix(0.35, 1.0, top) * blot * stainAmt;
    alb   = mix(alb, uC[2] * (0.7 + 0.6 * run), st * 0.85);
    rough = mix(rough, rough * 0.90 + 0.05, st * 0.6);
    /* efflorescence: pale mineral bloom in a few patches */
    float eff = ss(0.66, 0.90, tfbm2(uv, vec2(5.0), 4, 0.5, sd + 721.0) * 0.5 + 0.5) * stainAmt;
    alb = mix(alb, vec3(0.74, 0.73, 0.70), eff * 0.35);
    rough += eff * 0.08;
  }

  /* ---- surface drift: blown dust, damp patches, polished walking lines ---
     uP[4].y. The single loudest reason a paved plaza reads as a grey plane is
     that concrete in the real world is *never* one value across 40 m — it holds
     dust in the lee of things, stays damp where it never sees sun, and polishes
     where feet and tyres run. These are 0.3-1.5 m features, which is exactly the
     band that survives minification out to 30 m and gives the ground its
     high-frequency energy at gameplay distance. */
  if (uP[4].y > 0.001){
    float drift = uP[4].y;
    vec2 warp = vec2(tfbm2(uv, vec2(3.5), 4, 0.5, sd + 811.0),
                     tfbm2(uv, vec2(3.5), 4, 0.5, sd + 821.0)) * 0.17;
    vec3 wD = tvor(uv + warp, 3.0, sd + 831.0);
    float blotch = ss(0.56, 0.10, wD.x) * (0.35 + 0.65 * h11(wD.z * 17.1 + sd));
    float band  = tfbm2(uv, vec2(1.7, 6.0), 4, 0.55, sd + 841.0) * 0.5 + 0.5;
    float fleck = ss(0.60, 0.86, tfbm2(uv, vec2(26.0), 4, 0.5, sd + 851.0) * 0.5 + 0.5);
    float d = sat(blotch * 0.85 + ss(0.44, 0.80, band) * 0.55) * drift;
    alb   = mix(alb, uC[2] * 1.45, d * 0.55);
    alb   = mix(alb, uC[1] * 1.20, fleck * drift * 0.30);
    rough = mix(rough, min(rough + 0.12, 1.0), d * 0.7);
    h    -= d * 0.008;
    ao   *= 1.0 - d * 0.18;
  }

  /* ---- fine surface speckle: the top skin's sand fines and dark flecks ---
     uP[5].x. This is the 0.5-2 cm band. It is the band that decides whether a
     paved surface still reads as concrete at 3 m — coarser features are the
     "material", but a screen-space high-pass at gameplay distance only sees
     this one, and with nothing in it the plaza measures flatter than the sky. */
  if (uP[5].x > 0.001){
    float sp = uP[5].x;
    /* THE 240-CELL BAND IS THE "TELEVISION STATIC", AND IT IS AN AMPLITUDE PROBLEM.
     *
     * Ablated on level_interior, whose ceiling and floor are both concrete_floor
     * (picked by raycast, not assumed). Per-texel high-frequency energy, measured as the
     * mean |delta| to the left-hand neighbour over (500,60)-(1400,260):
     *
     *   base                                          9.58     <- static
     *   detail albedo off / detail layer off          9.59
     *   octave off / macro off                        9.51 / 9.79
     *   normalScale 0                                 9.58
     *   base texture magnified 8x (uUvXform * 0.12)   4.19     <- only this moved it
     *
     * A quiet dielectric in the same frame reads 3.1 and the stucco wall 3.8, so the
     * ceiling was carrying three times the frame's own noise floor. Every RUNTIME layer
     * is innocent; the energy is baked into the map at a frequency that lands on one
     * screen pixel at 4-6 m, and the 240-cell band is where it lives: 240 cells over a
     * 2.4 m tile is a 1 cm cell, and ss(0.34, 0.06, ...) keeps only the tiny bright
     * core of each one, so what the mip chain gets handed is a sparse field of near-white
     * points. Sparse + high contrast is the definition of salt-and-pepper.
     *
     * The cure is amplitude and coverage, not deletion: the cores are opened up
     * (0.34 -> 0.46 and 0.30 -> 0.42, i.e. the fleck covers more of its cell) and the
     * tint multipliers are pulled in hard (1.55 -> 1.16, 1.30 -> 1.12). Same band, same
     * spatial statistics, a third of the contrast — which is what "sand fines in a
     * concrete top skin" actually looks like. The 64-cell chunk band, which is the one
     * that survives minification and gives the plaza its read at 20 m, is untouched. */
    vec3 wF = tvor(uv, 240.0, sd + 907.0);
    vec3 wG = tvor(uv, 128.0, sd + 911.0);
    vec3 wH = tvor(uv, 64.0, sd + 917.0);
    float lite = ss(0.46, 0.10, wF.x) * step(0.40, wF.z);
    float dark = ss(0.42, 0.11, wG.x) * step(0.56, wG.z);
    /* a third, coarser fleck band: the 2-5 cm chips and dropped aggregate that a
       screen-space high-pass still sees from 6 m, where the 240-cell band is already
       into the mips. This is the band the plaza deck was missing.
       Core opened 0.26 -> 0.40 for the same reason as the two above: a chip that is a
       thin bright core inside its cell is a point, and a field of points is static. A
       chip that fills half its cell is a chip. */
    float chunk = ss(0.40, 0.09, wH.x) * step(0.63, wH.z);
    /* The film band is deliberately the LOWEST frequency of the three. fbm triples
       its frequency band over three octaves, so at 150 the top octave was 600 cycles —
       1.7 texels on a 1024 map — and a high-amplitude signal there does not read as
       fine aggregate, it reads as salt-and-pepper pixel noise, which is what the
       interior ceiling showed. The energy the plaza needs comes from the chunk band
       instead, which sits at 64 cells and survives at every resolution in the set. */
    float film = tfbm2(uv, vec2(80.0), 3, 0.5, sd + 913.0);
    alb  *= 1.0 + (film - 0.5) * 0.34 * sp;
    alb   = mix(alb, uC[1] * 1.16, lite * sp * 0.50);
    alb   = mix(alb, uC[2] * 1.12, dark * sp * 0.46);
    /* WAS uC[1] * (0.80 + 0.85 * rnd) mixed at chunk * sp * 0.72 — on concrete_floor
       (sp 1.35) that is a 0.97 mix, i.e. an almost total replacement of the paste with a
       colour running to 1.65 x the PALE tone, against a base that is the dark one. A
       near-binary swap between (89,86,78) and (233,223,200) on a 3 cm cell is the single
       largest high-frequency term in this generator and it is most of what the interior
       ceiling was showing. Range pulled to 0.74-1.30 and the mix to a half. */
    alb   = mix(alb, uC[1] * (0.74 + 0.56 * h11(wH.z * 23.0 + sd)), chunk * sp * 0.40);
    /* The 240- and 128-cell flecks contribute a THIRD of the height they used to. Their
       cells are 1-2 cm, i.e. 8-17 texels at 2048, but their cores are narrow, so in the
       normal map they were sharp pips; the 64-cell chunk band is broad enough to keep
       its full relief and it is the one that reads at gameplay distance anyway. */
    h    += lite * 0.004 * sp - dark * 0.005 * sp + chunk * 0.024 * sp;
    ao   *= 1.0 - dark * 0.20 * sp - chunk * 0.16 * sp;
    rough += (film - 0.5) * 0.09 * sp + chunk * 0.13 * sp;
  }

  /* ---- grime accumulating in the height minima (cavity dirt) ----------- */
  float cav = ss(0.62, 0.44, h);
  alb *= 1.0 - cav * 0.20;

  s.albedo = max(alb, vec3(0.005));
  s.rough  = clamp(rough, 0.06, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * BRICK
 * ===================================================================== */
brick: /* glsl */`
void surface(vec2 uv, inout Surf s){
  vec2 counts = uP[0].xy;          // bricks across, courses down
  float mortarW = uP[0].z, chip = uP[0].w;
  float sd = uSeed;
  float aspect = counts.y / counts.x;

  Lat L = lattice(uv, counts, 0.5, aspect);
  float id = L.id;

  /* Mortar joint. "Uniform mortar" was the complaint and it was fair: one wobble
   * frequency and one width for every joint in the wall. A hand-laid joint varies on
   * three scales — the trowel wanders (fine), the bed thickness drifts along a course
   * (coarse), and the raking depth changes per brick — so all three are here. */
  float wob  = tfbm2(uv, vec2(70.0), 4, 0.5, sd + 3.0) * 0.16;
  float wob2 = tfbm2(uv, vec2(6.0, 2.0), 3, 0.55, sd + 4.0) * 0.5;
  float jointW = mortarW * (0.72 + 0.56 * h11(L.cell.y * 11.7 + sd) + wob2 * 0.30);
  float b = L.border + wob * 0.010 * counts.y;
  float mortar = ss(jointW, jointW * 0.35, b);

  /* --- brick body ------------------------------------------------------- */
  float bn  = tfbm2(uv, vec2(50.0), 4, 0.5, sd + id * 51.0);
  float bn2 = tfbm2(uv, vec2(24.0), 4, 0.55, sd + id * 91.0 + 7.0);
  vec3 bcol = mix(uC[0], uC[1], sat(id * 1.35 - 0.18));
  bcol *= 1.0 + bn * 0.13 + bn2 * 0.11;
  /* whole bricks fired hotter/cooler — the single biggest cue that it is not a decal */
  bcol *= 0.62 + 0.76 * h11(id * 61.0 + 3.0);
  /* Per-brick DESATURATION, 0.10-0.52 instead of a flat 0.18. This is the term that
   * decides whether a wall reads as new engineering brick or as a weathered street:
   * in a real wall some bricks have burnt out to grey, some are still clay-coloured,
   * and the spread between them is wider than the spread within any one brick. */
  bcol = mix(bcol, vec3(dot(bcol, vec3(0.3, 0.59, 0.11))), 0.10 + 0.42 * h11(id * 29.0));
  /* per-brick hue drift: kiln position shifts clay between ochre and grey-pink */
  float hj = h11(id * 43.7 + 11.0) - 0.5;
  bcol *= vec3(1.0 + hj * 0.16, 1.0 + hj * 0.03, 1.0 - hj * 0.14);
  /* fired-clay speckle + sand inclusions */
  vec3 wS = tvor(uv, 170.0, sd + 21.0);
  float speck = ss(0.42, 0.06, wS.x);
  bcol *= 1.0 - speck * 0.14;
  bcol = mix(bcol, bcol * vec3(1.10, 0.98, 0.90), ss(0.4, 0.9, h11(id * 13.0)) * 0.5);

  float bh = 0.72 + bn * 0.020 + bn2 * 0.018 + (h11(id * 7.3) - 0.5) * 0.030;
  float br = 0.66 + bn * 0.10 + (h11(id * 3.1) - 0.5) * 0.10;

  /* chipped corners and broken faces */
  vec3 wC = tvor(uv, 90.0, sd + 41.0);
  float cornerM = ss(0.30, 0.02, b) * step(0.62, h11(id * 17.0));
  float chipN = ss(0.30, 0.06, wC.x) * step(0.55, wC.z);
  float chipped = chipN * cornerM * chip;
  bh  -= chipped * 0.10;
  bcol = mix(bcol, bcol * 1.25, chipped * 0.6);
  br  += chipped * 0.12;

  /* --- mortar body ------------------------------------------------------ */
  float mn = tfbm2(uv, vec2(90.0), 4, 0.5, sd + 61.0);
  vec3 mwCol = uC[2] * (1.0 + mn * 0.24);
  vec3 wM = tvor(uv, 160.0, sd + 63.0);
  mwCol *= 1.0 - ss(0.4, 0.05, wM.x) * 0.20;
  /* raking depth varies per brick: some joints struck flush, some recessed 8 mm */
  float rake = 0.55 + 0.9 * h11(id * 83.0 + 5.0);
  float mh = 0.50 + mn * 0.020 - ss(jointW * 0.6, 0.0, b) * 0.05 * rake;
  float mr = 0.92 + mn * 0.06;

  vec3 alb   = mix(bcol, mwCol, mortar);
  float h    = mix(bh, mh, mortar);
  float rough = mix(br, mr, mortar);
  float ao   = 1.0 - mortar * 0.42;

  /* --- weathering: soot, water runs, efflorescence, moss in joints ------ */
  float run = streaks(uv, 20.0, 1.3, sd + 81.0);
  float grime = sat(run * 0.9) * ss(0.35, 0.9, tfbm2(uv, vec2(2.6), 4, 0.5, sd + 83.0) * 0.5 + 0.5);
  alb = mix(alb, uC[3], grime * uP[1].x);
  rough = mix(rough, 0.94, grime * uP[1].x * 0.6);

  float eff = ss(0.62, 0.92, tfbm2(uv, vec2(4.0), 4, 0.5, sd + 87.0) * 0.5 + 0.5);
  alb = mix(alb, vec3(0.70, 0.69, 0.66), eff * uP[1].y);

  float moss = ss(0.55, 0.9, tfbm2(uv, vec2(7.0), 4, 0.5, sd + 91.0) * 0.5 + 0.5) * mortar;
  alb = mix(alb, vec3(0.055, 0.075, 0.035), moss * uP[1].z);

  float cav = ss(0.68, 0.48, h);
  alb *= 1.0 - cav * 0.22;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.2, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * ASPHALT
 * ===================================================================== */
asphalt: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float macro = tfbm2(uv, vec2(3.0), 5, 0.55, sd);
  float mid   = tfbm2(uv, vec2(16.0), 5, 0.5, sd + 11.0);

  /* bitumen matrix */
  vec3 alb = uC[0] * (1.0 + macro * 0.22 + mid * 0.14);
  float h = 0.55 + macro * 0.03 + mid * 0.02;
  float rough = 0.86 + mid * 0.06;

  /* aggregate chips of many sizes */
  vec3 w1 = tvor(uv, 60.0, sd + 3.0);
  vec3 w2 = tvor(uv, 130.0, sd + 7.0);
  vec3 w3 = tvor(uv, 300.0, sd + 13.0);
  float c1 = ss(0.34, 0.10, w1.x) * step(0.40, w1.z);
  float c2 = ss(0.38, 0.10, w2.x) * step(0.50, w2.z);
  float c3 = ss(0.42, 0.10, w3.x);

  vec3 stone1 = uC[1] * (0.55 + 0.9 * h11(w1.z * 41.0));
  vec3 stone2 = uC[1] * (0.55 + 0.9 * h11(w2.z * 17.0));
  alb = mix(alb, stone1, c1 * 0.85);
  alb = mix(alb, stone2, c2 * 0.6);
  alb *= 1.0 - c3 * 0.10;
  h += c1 * 0.045 + c2 * 0.026 + c3 * 0.008;
  rough = mix(rough, 0.62, c1 * 0.8);
  rough = mix(rough, 0.70, c2 * 0.5);

  /* polished wheel tracks — smoother, darker, shinier */
  float track = ss(0.55, 0.9, tfbm2(uv, vec2(1.6, 0.6), 3, 0.5, sd + 31.0) * 0.5 + 0.5) * uP[0].x;
  rough = mix(rough, 0.42, track * 0.7);
  alb *= 1.0 - track * 0.16;
  h = mix(h, h * 0.5 + 0.30, track * 0.5);

  /* cracks and patched repairs */
  float cr = crackNet(uv, vec2(5.0), 0.905, sd + 51.0) + crackNet(uv, vec2(14.0), 0.945, sd + 61.0) * 0.6;
  cr = sat(cr) * uP[0].y;
  h -= cr * 0.12;
  alb *= 1.0 - cr * 0.6;
  rough += cr * 0.06;

  float repair = ss(0.6, 0.75, tfbm2(uv, vec2(2.2), 3, 0.5, sd + 71.0) * 0.5 + 0.5) * uP[0].z;
  alb = mix(alb, uC[0] * 0.6, repair);
  rough = mix(rough, 0.9, repair);

  /* dust film */
  float dust = ss(0.4, 0.85, tfbm2(uv, vec2(6.0), 4, 0.5, sd + 81.0) * 0.5 + 0.5) * uP[0].w;
  alb = mix(alb, uC[2], dust * 0.5);
  rough += dust * 0.05;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.15, 1.0);
  s.metal  = 0.0;
  s.ao     = 1.0 - ss(0.55, 0.40, h) * 0.35;
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * GRANULAR — sand, gravel, dirt
 * ===================================================================== */
granular: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float stoneAmt = uP[0].x;    // 0 = pure sand, 1 = gravel
  float dune     = uP[0].y;    // ripple strength
  float clumps   = uP[0].z;    // dirt clods / debris
  float wetness  = uP[0].w;
  float sd = uSeed;

  float macro = tfbm2(uv, vec2(2.5), 5, 0.58, sd);
  float mid   = tfbm2(uv, vec2(11.0), 5, 0.5, sd + 11.0);
  float fine  = tfbm2(uv, vec2(55.0), 4, 0.5, sd + 21.0);

  vec3 alb = uC[0] * (1.0 + macro * 0.24 + mid * 0.14 + fine * 0.07);
  float h  = 0.5 + macro * 0.10 + mid * 0.045 + fine * 0.015;
  float rough = 0.90 + fine * 0.05;
  float ao = 1.0;

  /* wind ripples */
  if (dune > 0.001){
    float warp = tfbm2(uv, vec2(4.0), 3, 0.5, sd + 31.0) * 0.10;
    float rip = sin((uv.x * 1.0 + uv.y * 0.35 + warp) * TAU * 14.0) * 0.5 + 0.5;
    rip = pow(rip, 1.7);
    h += (rip - 0.5) * 0.055 * dune;
    alb *= 1.0 + (rip - 0.5) * 0.10 * dune;
  }

  /* individual grains */
  vec3 wG = tvor(uv, 420.0, sd + 41.0);
  float grain = ss(0.45, 0.05, wG.x);
  h += grain * 0.006;
  alb *= 1.0 - grain * 0.10 + grain * h11(wG.z * 13.0) * 0.22;
  rough += grain * 0.03;

  /* pebbles / gravel */
  if (stoneAmt > 0.001){
    vec3 w1 = tvor(uv, 34.0, sd + 51.0);
    vec3 w2 = tvor(uv, 72.0, sd + 57.0);
    float p1 = ss(0.40, 0.08, w1.x);
    float p2 = ss(0.44, 0.10, w2.x) * step(0.35, w2.z);
    vec3 c1 = mix(uC[1], uC[2], h11(w1.z * 27.0));
    vec3 c2 = mix(uC[1], uC[2], h11(w2.z * 13.0));
    alb = mix(alb, c1 * (0.6 + 0.8 * h11(w1.z * 3.0)), p1 * stoneAmt);
    alb = mix(alb, c2 * (0.6 + 0.8 * h11(w2.z * 5.0)), p2 * stoneAmt * 0.7);
    h += (p1 * 0.075 + p2 * 0.040) * stoneAmt;
    rough = mix(rough, 0.62, p1 * stoneAmt * 0.8);
    ao *= 1.0 - ss(0.28, 0.44, w1.x) * ss(0.55, 0.42, w1.x) * stoneAmt * 0.5;
  }

  /* clods, twigs, dark organic debris */
  if (clumps > 0.001){
    vec3 wD = tvor(uv, 20.0, sd + 61.0);
    float clod = ss(0.34, 0.10, wD.x) * step(0.45, wD.z);
    alb = mix(alb, uC[2] * 0.55, clod * clumps);
    h += clod * 0.05 * clumps;
    rough += clod * 0.04 * clumps;
    float dark = ss(0.55, 0.92, tfbm2(uv, vec2(9.0), 4, 0.5, sd + 63.0) * 0.5 + 0.5);
    alb *= 1.0 - dark * 0.28 * clumps;
  }

  /* damp patches: darker, much smoother */
  if (wetness > 0.001){
    float wet = ss(0.45, 0.85, tfbm2(uv, vec2(3.5), 4, 0.5, sd + 71.0) * 0.5 + 0.5) * wetness;
    alb *= 1.0 - wet * 0.45;
    rough = mix(rough, 0.35, wet);
  }

  float cav = ss(0.55, 0.35, h);
  ao  *= 1.0 - cav * 0.35;
  alb *= 1.0 - cav * 0.14;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.2, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * PAINTED METAL — chipping through primer to bare steel, weld seams, dents
 * ===================================================================== */
metal_painted: /* glsl */`
void surface(vec2 imUv, inout Surf s){
  vec2 uv = imUv;
  float chipAmt  = uP[0].x;
  float rustAmt  = uP[0].y;
  float weldAmt  = uP[0].z;
  float dentAmt  = uP[0].w;
  float scratches= uP[1].x;
  float gloss    = uP[1].y;     // 0 matte .. 1 semi-gloss
  float panelAmt = uP[1].z;
  float grimeAmt = uP[1].w;
  float sd = uSeed;

  /* --- bare steel underneath ------------------------------------------- */
  float mn = tfbm2(uv, vec2(150.0, 40.0), 4, 0.5, sd + 3.0);
  vec3 steel = vec3(0.38, 0.39, 0.40) * (0.90 + mn * 0.16);
  float steelR = 0.30 + mn * 0.10;
  float steelM = 1.0;

  /* rolling / grinding lines */
  float roll = tfbm2(uv, vec2(600.0, 12.0), 3, 0.5, sd + 5.0);
  steelR += roll * 0.10;

  /* --- primer ----------------------------------------------------------- */
  vec3 primer = uC[2];
  float primerR = 0.72;

  /* --- top coat --------------------------------------------------------- */
  float pn  = tfbm2(uv, vec2(40.0), 4, 0.5, sd + 11.0);
  float pn2 = tfbm2(uv, vec2(260.0), 3, 0.5, sd + 13.0);
  vec3 paint = uC[0] * (1.0 + pn * 0.09 + pn2 * 0.04);
  float paintR = mix(0.62, 0.24, gloss) + pn * 0.05 + pn2 * 0.05;

  float h = 0.66 + pn * 0.010;
  float ao = 1.0;

  /* --- panel seams / rivet lines ---------------------------------------- */
  if (panelAmt > 0.001){
    vec2 g = abs(fract(uv * vec2(1.0, 2.0)) - 0.5);
    float seam = ss(0.020, 0.0, min(g.x, g.y));
    h  -= seam * 0.05 * panelAmt;
    ao *= 1.0 - seam * 0.5 * panelAmt;
    /* rivets along the seam */
    vec2 rp = fract(uv * vec2(24.0, 24.0)) - 0.5;
    float onSeam = ss(0.05, 0.0, min(g.x, g.y));
    float riv = ss(0.30, 0.16, length(rp)) * onSeam;
    h += riv * 0.05 * panelAmt;
  }

  /* --- weld seams: stacked-dime bead ------------------------------------ */
  float weld = 0.0;
  if (weldAmt > 0.001){
    float lineY = 0.5;
    float wob = tfbm2(uv, vec2(9.0, 2.0), 3, 0.5, sd + 17.0) * 0.012;
    float d = abs(fract(uv.y + wob + 0.25) - 0.5) * 2.0;   // distance from a horizontal band
    float band = ss(0.10, 0.0, d);
    float dimes = sin(uv.x * TAU * 46.0) * 0.5 + 0.5;
    weld = band * weldAmt;
    h  += weld * (0.055 + dimes * 0.030 * ss(0.5, 0.0, d));
    float heatTint = ss(0.22, 0.06, d) * weldAmt;
    paint = mix(paint, mix(vec3(0.10, 0.09, 0.11), vec3(0.16, 0.10, 0.05), dimes), weld * 0.85);
    paintR = mix(paintR, 0.52 + dimes * 0.14, weld);
    steel = mix(steel, vec3(0.34, 0.30, 0.28), weld * 0.6);
    /* burn halo */
    float halo = ss(0.34, 0.12, d) * (1.0 - band);
    paint = mix(paint, paint * vec3(0.75, 0.72, 0.80), halo * 0.6 * weldAmt);
  }

  /* --- dents ------------------------------------------------------------ */
  if (dentAmt > 0.001){
    vec3 wD = tvor(uv, 6.0, sd + 21.0);
    float dent = ss(0.42, 0.05, wD.x) * step(0.7, wD.z);
    h -= dent * 0.06 * dentAmt;
  }

  /* --- paint chipping: coverage mask ------------------------------------ */
  float wearBase = tfbm2(uv, vec2(5.0), 5, 0.58, sd + 31.0) * 0.5 + 0.5;
  float wearEdge = tridge(uv, vec2(15.0), 4, 0.5, sd + 33.0);
  float wearFine = tfbm2(uv, vec2(60.0), 4, 0.5, sd + 35.0) * 0.5 + 0.5;
  /* w rises where the panel has been rubbed; high w = paint gone */
  float w = wearBase * 0.55 + wearEdge * 0.25 + wearFine * 0.20;

  float t0 = mix(1.35, 0.30, chipAmt);
  float topCoat  = 1.0 - ss(t0, t0 + 0.05, w);          // 1 = paint intact
  float primerOn = 1.0 - ss(t0 + 0.09, t0 + 0.15, w);   // 1 = at least primer

  /* --- BURNISHED STEEL: the polished core of a wear patch, and the rubbed arris
   *     on every edge a hand, a shoulder or a bumper passes. uP[2].x.
   *
   * This material and its 'metal' alias clothe 183 objects in the map, and FINDINGS
   * F13 is unambiguous about why the whole build has no highlight anywhere: "no
   * window returns a sun reflection, no surface is wet, no metal is bare". A painted
   * railing IS bare metal in the two places it gets touched — the chip centres and the
   * arris — and burnished steel is the smoothest surface in an urban street: an actual
   * mirror at roughness ~0.11 against a scene whose 60 of 69 materials sit above 0.9.
   *
   * It is deliberately small in area. The point is a handful of pixels that reach the
   * top of the range, which is what a photograph has and this build has never had; it
   * is not an excuse to make the street shiny. It also SUPPRESSES rust below, because
   * a surface rubbed often enough to burnish is a surface that never gets to rust. */
  float burnAmt = uP[2].x;
  float burnish = 0.0;
  if (burnAmt > 0.001){
    float core  = ss(t0 - 0.05, t0 + 0.07, w);
    float arris = ss(0.87, 0.99, wearEdge);
    burnish = sat(max(core, arris * 0.92)) * burnAmt;
  }

  vec3  alb   = mix(mix(steel, primer, primerOn), paint, topCoat);
  float rough = mix(mix(steelR, primerR, primerOn), paintR, topCoat);
  float metal = mix(mix(steelM, 0.0, primerOn), 0.0, topCoat);

  /* chipped-away areas sit slightly lower; the paint edge lifts */
  h -= (1.0 - topCoat) * 0.016;
  float edge = ss(0.0, 0.4, topCoat) * ss(0.75, 0.35, topCoat);
  h  += edge * 0.010;
  ao *= 1.0 - edge * 0.30;

  /* --- rust blooming from the exposed steel ----------------------------- */
  if (rustAmt > 0.001){
    float rn = tfbm2(uv, vec2(18.0), 5, 0.55, sd + 41.0) * 0.5 + 0.5;
    float bloom = sat((1.0 - primerOn) * 1.15 + (1.0 - topCoat) * 0.35);
    float rust = ss(0.55, 0.95, bloom * 0.85 + rn * 0.35) * rustAmt;
    /* streaking rust running down from the chip */
    float rs = streaks(uv, 30.0, 1.5, sd + 43.0);
    float bleed = sat(rs * 0.7) * ss(0.5, 0.95, bloom) * rustAmt;
    float rt = sat(rust + bleed * 0.6) * (1.0 - burnish * 0.92);
    vec3 rustCol = mix(uC[1] * 0.55, uC[1], rn);
    alb   = mix(alb, rustCol, rt);
    rough = mix(rough, 0.90 + rn * 0.08, rt);
    metal = metal * (1.0 - sat(rt * 1.6));   // rust is a DIELECTRIC
    h    += rt * 0.020 * rn;
    ao   *= 1.0 - rt * 0.18;
  }

  /* --- apply the burnish AFTER rust, so a rubbed patch stays bright steel --- */
  if (burnish > 0.001){
    vec3 bright = vec3(0.60, 0.61, 0.63) * (0.90 + mn * 0.18);
    alb   = mix(alb, bright, burnish * 0.92);
    rough = mix(rough, 0.105 + abs(roll) * 0.07, burnish * 0.95);
    metal = mix(metal, 1.0, burnish);
    h    -= burnish * 0.004;
  }

  /* --- scratches down to metal ------------------------------------------ */
  if (scratches > 0.001){
    vec3 sc = tvorA(uv, vec2(9.0, 130.0), sd + 51.0);
    float line = ss(0.055, 0.0, sc.x) * step(0.55, sc.z);
    vec3 sc2 = tvorA(uv, vec2(140.0, 7.0), sd + 53.0);
    float line2 = ss(0.05, 0.0, sc2.x) * step(0.72, sc2.z);
    float scr = sat(line + line2) * scratches;
    alb   = mix(alb, steel * 1.15, scr * 0.8);
    metal = mix(metal, 1.0, scr * 0.75);
    rough = mix(rough, 0.24, scr * 0.7);
    h    -= scr * 0.006;
  }

  /* --- grime in the recesses -------------------------------------------- */
  float cav = ss(0.66, 0.50, h);
  float grime = sat(cav * 1.2 + streaks(uv, 16.0, 1.0, sd + 61.0) * 0.35) * grimeAmt;
  alb   = mix(alb, uC[3], grime * 0.45);
  rough = mix(rough, 0.92, grime * 0.4);
  metal = metal * (1.0 - grime * 0.35);

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.05, 1.0);
  s.metal  = sat(metal);
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * HEAVILY RUSTED METAL
 * ===================================================================== */
metal_rusted: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float coverage = uP[0].x;    // 0..1 how much of the panel is rust
  float pitting  = uP[0].y;
  float flaking  = uP[0].z;
  float weldAmt  = uP[0].w;

  /* base steel */
  float mn = tfbm2(uv, vec2(180.0, 45.0), 4, 0.5, sd + 3.0);
  vec3 steel = vec3(0.27, 0.275, 0.285) * (0.85 + mn * 0.22);
  float steelR = 0.54 + mn * 0.16;

  /* multi-scale rust field */
  float r1 = tfbm2(uv, vec2(3.5), 5, 0.58, sd + 11.0) * 0.5 + 0.5;
  float r2 = tfbm2(uv, vec2(14.0), 5, 0.5, sd + 13.0) * 0.5 + 0.5;
  float r3 = tturb(uv, vec2(48.0), 4, 0.5, sd + 17.0);
  float rf = r1 * 0.55 + r2 * 0.30 + r3 * 0.15;

  float rustM = ss(mix(1.05, 0.02, coverage), mix(1.15, 0.34, coverage), rf);

  /* rust colour: dark iron oxide in the deep areas, bright orange at the frontier */
  vec3 deep   = uC[0];              // dark brown
  vec3 bright = uC[1];              // orange
  vec3 pale   = uC[2];              // pale ochre bloom
  float band = ss(0.25, 0.62, rf);
  vec3 rustCol = mix(deep, bright, band);
  rustCol = mix(rustCol, pale, ss(0.70, 0.95, rf) * 0.55);
  rustCol *= 0.80 + r3 * 0.45;

  float h = 0.60 + mn * 0.006;
  float ao = 1.0;

  /* rust is volumetric: it sits proud of the steel and is pitted */
  h += rustM * (0.016 + r3 * 0.024);

  /* pitting: deep craters eaten into the steel */
  if (pitting > 0.001){
    vec3 wP = tvor(uv, 110.0, sd + 21.0);
    float pit = ss(0.30, 0.02, wP.x) * step(0.42, wP.z);
    vec3 wP2 = tvor(uv, 38.0, sd + 23.0);
    float pit2 = ss(0.28, 0.04, wP2.x) * step(0.62, wP2.z);
    float p = sat(pit * 0.6 + pit2 * 0.8) * pitting * mix(0.20, 1.0, rustM);
    h  -= p * 0.06;
    ao *= 1.0 - p * 0.6;
    rustCol *= 1.0 - p * 0.45;
  }

  /* flaking scale: hard-edged plates lifting off */
  if (flaking > 0.001){
    vec3 wF = tvor(uv, 26.0, sd + 31.0);
    float plate = ss(0.06, 0.16, wF.x);
    float lip = ss(0.02, 0.09, wF.x) * ss(0.16, 0.09, wF.x);
    float fl = flaking * rustM;
    h  += (plate - 0.5) * 0.016 * fl + lip * 0.018 * fl;
    ao *= 1.0 - lip * 0.35 * fl;
    rustCol = mix(rustCol, rustCol * 1.25, lip * 0.5 * fl);
  }

  /* weld bead */
  if (weldAmt > 0.001){
    float wob = tfbm2(uv, vec2(9.0, 2.0), 3, 0.5, sd + 41.0) * 0.012;
    float d = abs(fract(uv.y + wob + 0.5) - 0.5) * 2.0;
    float band2 = ss(0.09, 0.0, d);
    float dimes = sin(uv.x * TAU * 42.0) * 0.5 + 0.5;
    h += band2 * (0.05 + dimes * 0.028 * ss(0.5, 0.0, d)) * weldAmt;
  }

  vec3  alb   = mix(steel, rustCol, rustM);
  float rough = mix(steelR, 0.88 + r3 * 0.10, rustM);
  float metal = 1.0 - sat(rustM * 1.25);      // rust: dielectric

  /* residual mill scale — dark, near-metal, low roughness patches */
  float scale = ss(0.60, 0.90, tfbm2(uv, vec2(9.0), 4, 0.5, sd + 51.0) * 0.5 + 0.5) * (1.0 - rustM);
  alb   = mix(alb, vec3(0.048, 0.046, 0.045), scale * 0.85);
  rough = mix(rough, 0.40, scale * 0.8);
  metal = mix(metal, 1.0, scale * 0.5);

  /* grime */
  float cav = ss(0.62, 0.48, h);
  alb *= 1.0 - cav * 0.24;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.08, 1.0);
  s.metal  = sat(metal);
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * BRUSHED / MACHINED STEEL — anisotropic. also the gun-metal base.
 * ===================================================================== */
brushed: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float brushF   = uP[0].x;    // brush line density
  float rBase    = uP[0].y;
  float aniso    = uP[0].z;    // roughness spread along the grain
  float wearAmt  = uP[0].w;
  float bluing   = uP[1].x;    // gun bluing / parkerising darkness
  float machining= uP[1].y;    // circular/linear machining marks
  float oilAmt   = uP[1].z;
  float pitAmt   = uP[1].w;
  /* uP[2].x  polish 0..1 — how much of the surface is BARE metal rather than a
   *          conversion coating. This is the parameter FINDINGS F13 is asking for:
   *          "no metal is bare" is why the whole build has no specular. A polished
   *          band takes roughness under 0.15, which is the only roughness at which a
   *          surface returns a recognisable image of the sky instead of a grey wash.
   * uP[2].y  handling smudge amount (0 = a just-cleaned bolt, 1 = a carried rifle)
   * uP[2].z  broad roughness zoning — how much the finish varies across the part */
  float polish   = uP[2].x;
  float smudgeAmt= uP[2].y;
  float zoneAmt  = uP[2].z;

  /* --- brushed grain: long thin anisotropic scratches -------------------
   * Octave counts are deliberately small. fbm doubles the frequency every octave, so
   * tfbm2(brushF, 4 octaves) runs its top band at 8x brushF — at the old brushF 380
   * on a 512 map that was 3040 cycles, six per texel, which is not detail, it is
   * uniform noise that mips straight back to flat grey. That is why this material
   * rendered as chrome. */
  float g1 = tfbm2(uv, vec2(brushF, brushF * 0.012), 3, 0.55, sd + 3.0);
  float g2 = tfbm2(uv, vec2(brushF * 2.2, brushF * 0.03), 2, 0.5, sd + 5.0);
  float g3 = tfbm2(uv, vec2(brushF * 4.0, brushF * 0.06), 1, 0.5, sd + 7.0);
  float grain = g1 * 0.46 + g2 * 0.34 + g3 * 0.20;

  /* discrete deeper grooves, running the same way as the grain */
  vec3 gr = tvorA(uv, vec2(max(4.0, brushF * 0.9), 2.0), sd + 11.0);
  float groove = ss(0.18, 0.0, gr.x) * step(0.30, gr.z);

  vec3 base = uC[0];
  /* The grooves have to be real geometry in the normal map or there is nothing for a
   * highlight to stretch along — 4x the old relief, and the roughness spread along the
   * grain is what turns a round specular lobe into a brushed streak.
   *
   * "Still reads as chrome, not brushed." Two more things were missing and both are
   * here now: the roughness spread is asymmetric (a groove floor is polished by the
   * abrasive, a groove wall is torn, so the distribution is skewed, not a symmetric
   * +-aniso wobble), and the material now also carries a real ANISOTROPIC GGX lobe,
   * authored on the THREE material — a scalar roughness cannot stretch a highlight no
   * matter what you put in the map. */
  float h = 0.62 + grain * 0.052 - groove * 0.070;
  float skew = grain * 0.5 + 0.5;
  float rough = rBase + (skew * skew * 2.0 - 0.5) * aniso + groove * 0.20;
  float metal = 1.0;
  float ao = 1.0 - groove * 0.18;
  vec3 alb = base * (1.0 + grain * 0.12);

  /* Broad finish zoning: no real part is one roughness end to end.
   *
   * "metal_bare p99 0.988, gun_steel_bare mean (0.742, 0.715, 0.680) at saturation
   * 0.085 — both read nickel plate, not steel", and "steel_brushed and metal_bare are
   * indistinguishable". FINDINGS F15 recorded the prescription and it was not carried
   * out: MACRO TONAL VARIATION ACROSS THE METAL'S OWN SURFACE PLUS A WIDER ROUGHNESS
   * FIELD — and explicitly NOT dimming, because dimming spends the p99 that the whole
   * F13 specular effort exists to buy. A chrome ball has no macro variation; a
   * phosphated barrel has a dozen.
   *
   * At (zn - 0.5) * 0.14 the tonal term was +-7 % of albedo over one 3-cycle field:
   * below the noise of the environment reflection itself, i.e. invisible. Now two
   * decorrelated fields — part scale (1.7) and hand-span scale (5.5) — at three times
   * the amplitude, with a small per-zone warm/cool shift so the surface is not one
   * colour lit two ways.
   *
   * Every one of the three terms is symmetric about its mean, so the DISTRIBUTION
   * widens while the mean does not move: p99 is preserved, p05 falls, and the gap
   * between them is what reads as a real, used piece of metal. */
  if (zoneAmt > 0.001){
    float zn = tfbm2(uv, vec2(1.7), 4, 0.55, sd + 71.0) * 0.5 + 0.5;
    float zm = tfbm2(uv, vec2(5.5), 3, 0.50, sd + 77.0) * 0.5 + 0.5;
    float z  = (zn - 0.5) * 0.68 + (zm - 0.5) * 0.32;
    rough += z * 0.62 * zoneAmt;
    alb   *= 1.0 + z * 0.62 * zoneAmt;
    alb   *= vec3(1.0 + z * 0.10 * zoneAmt, 1.0, 1.0 - z * 0.12 * zoneAmt);
  }

  /* --- machining marks (fine parallel tool passes) ---------------------- */
  if (machining > 0.001){
    /* 190 tool passes on a 512 map is 2.7 texels per cycle — the pattern folded into
       moire and averaged away. 60 holds at every resolution this family runs at. */
    float m = sin((uv.y + tfbm2(uv, vec2(30.0, 4.0), 3, 0.5, sd + 13.0) * 0.02) * TAU * 60.0) * 0.5 + 0.5;
    h += (m - 0.5) * 0.010 * machining;
    rough += (m - 0.5) * 0.08 * machining;
  }

  /* --- bluing / parkerising --------------------------------------------- */
  if (bluing > 0.001){
    float bn = tfbm2(uv, vec2(70.0), 4, 0.5, sd + 21.0) * 0.5 + 0.5;
    vec3 park = uC[1] * (0.75 + bn * 0.5);
    alb = mix(alb, park, bluing);
    rough = mix(rough, 0.44 + bn * 0.20, bluing * 0.85);
    /* parkerising is a phosphate crystal layer — micro texture */
    vec3 wc = tvor(uv, 190.0, sd + 23.0);
    float cryst = ss(0.45, 0.06, wc.x);
    h += cryst * 0.003 * bluing;
    rough += cryst * 0.06 * bluing;
    alb *= 1.0 - cryst * 0.08 * bluing;
  }

  /* --- edge wear: high points polished back to bright steel ------------- */
  if (wearAmt > 0.001){
    float wf = tfbm2(uv, vec2(6.0), 5, 0.58, sd + 31.0) * 0.5 + 0.5;
    float we = tridge(uv, vec2(18.0), 4, 0.5, sd + 33.0);
    float wear = ss(0.60, 0.86, wf * 0.7 + we * 0.4) * wearAmt;
    alb = mix(alb, vec3(0.52, 0.53, 0.55), wear * 0.8);
    rough = mix(rough, 0.20 + abs(grain) * aniso * 1.2, wear * 0.85);
    metal = 1.0;
    h += wear * 0.004;
  }

  /* --- corrosion pits --------------------------------------------------- */
  if (pitAmt > 0.001){
    vec3 wp = tvor(uv, 150.0, sd + 41.0);
    float pit = ss(0.22, 0.02, wp.x) * step(0.78, wp.z) * pitAmt;
    h -= pit * 0.05;
    ao *= 1.0 - pit * 0.5;
    alb = mix(alb, vec3(0.16, 0.10, 0.06), pit * 0.7);
    rough = mix(rough, 0.9, pit * 0.8);
    metal = mix(metal, 0.0, pit * 0.8);
  }

  /* --- oil film: darkens and smooths, kills a little metalness ---------- */
  if (oilAmt > 0.001){
    float o = ss(0.45, 0.9, tfbm2(uv, vec2(8.0), 4, 0.5, sd + 51.0) * 0.5 + 0.5) * oilAmt;
    rough = mix(rough, rough * 0.55, o);
    alb *= 1.0 - o * 0.12;
  }

  /* --- bare polished metal ----------------------------------------------
   * Barrel flats, bolt carrier rails, charging-handle latch, the crown, sling swivels:
   * surfaces where the coating has been worn or machined off and the metal underneath
   * is measurably smoother than anything else in the build. This is the specular
   * source F13 says the whole frame is missing, so the band is deliberately wide and
   * deliberately gets DOWN to 0.06 — a roughness at which the sky is an image rather
   * than a wash. */
  if (polish > 0.001){
    float pz = tfbm2(uv, vec2(2.4), 4, 0.55, sd + 81.0) * 0.5 + 0.5;
    float pk = ss(0.30, 0.72, pz) * polish;
    /* the abrasive still leaves its grain, it is just much finer */
    float fine = tfbm2(uv, vec2(brushF * 2.4, brushF * 0.02), 2, 0.5, sd + 83.0);
    alb = mix(alb, uC[0] * 1.28 + vec3(0.02), pk * 0.85);
    rough = mix(rough, 0.060 + abs(fine) * 0.085 + groove * 0.10, pk);
    metal = mix(metal, 1.0, pk);
    h += pk * 0.004;
  }

  /* --- fingerprints / handling smudge ----------------------------------- */
  float smudge = ss(0.55, 0.95, tfbm2(uv, vec2(16.0), 4, 0.5, sd + 61.0) * 0.5 + 0.5) * smudgeAmt;
  rough += smudge * 0.10 * (1.0 - bluing * 0.5);
  metal *= 1.0 - smudge * 0.10;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.035, 1.0);
  s.metal  = sat(metal);
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * METAL GRATE / MESH — alpha cut-out, thick bars
 * ===================================================================== */
grate: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  vec2 counts = uP[0].xy;
  float barW  = uP[0].z;      // half-width of a bar in cell units
  float rustAmt = uP[0].w;

  vec2 c = fract(uv * counts) - 0.5;
  float dx = abs(c.x), dy = abs(c.y);
  /* two bar families: heavy bearing bars (vertical), light cross rods */
  float bar1 = ss(barW, barW * 0.55, dx);
  float bar2 = ss(barW * 0.62, barW * 0.32, dy);
  float solid = sat(bar1 + bar2);

  float top = max(bar1, bar2 * 0.7);
  float h = 0.15 + top * 0.75;
  float ao = mix(0.25, 1.0, ss(0.0, 0.6, solid));

  /* Mill texture. WAS vec2(200,50) x 4 octaves = a 1600-cycle top band on a 512 map:
     nothing survived and the bars came out a flat painted-looking tone. */
  float mn = tfbm2(uv, vec2(50.0, 14.0), 3, 0.5, sd + 3.0);
  vec3 alb = uC[0] * (0.9 + mn * 0.2);
  /* bar grating is hot-dip galvanised: sharper than mill steel, so the highlight has
     to be tight enough to actually reflect the sky and read as metal */
  float rough = 0.28 + mn * 0.16;
  float metal = 1.0;

  /* galvanised spangle — the zinc crystal facets, each with its own micro-orientation */
  vec3 sp = tvor(uv, 60.0, sd + 7.0);
  float spangle = ss(0.42, 0.06, sp.x);
  alb *= 1.0 + spangle * 0.30 * (h11(sp.z * 17.0) - 0.4);
  rough += (h11(sp.z * 5.0) - 0.5) * 0.26 * spangle;
  h += (h11(sp.z * 29.0) - 0.5) * 0.014 * spangle;
  /* the rolled top face of every bar is polished by feet, the sides are not */
  float wearTop = ss(0.55, 0.95, top);
  rough = mix(rough, rough * 0.62, wearTop * 0.7);

  /* rust on the edges and where the coating failed */
  float rn = tfbm2(uv, vec2(20.0), 5, 0.5, sd + 11.0) * 0.5 + 0.5;
  float edge = ss(0.75, 0.35, solid);
  float rust = ss(0.42, 0.78, rn * 0.7 + edge * 0.55) * rustAmt;
  alb = mix(alb, uC[1] * (0.6 + rn * 0.8), rust);
  rough = mix(rough, 0.92, rust);
  metal *= 1.0 - sat(rust * 1.4);

  /* grime under the bars */
  alb *= 1.0 - (1.0 - ao) * 0.35;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.1, 1.0);
  s.metal  = sat(metal);
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = ss(0.34, 0.55, solid);
}
`,

/* ===================================================================== *
 * CORRUGATED METAL SHEET
 * ===================================================================== */
corrugated: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float ribs = uP[0].x;
  float rustAmt = uP[0].y;
  float paintAmt = uP[0].z;
  float dentAmt = uP[0].w;

  /* trapezoidal profile */
  float t = fract(uv.x * ribs);
  float tri = abs(t - 0.5) * 2.0;
  float prof = 1.0 - smoothstep(0.15, 0.85, tri);
  float h = 0.18 + prof * 0.66;

  /* the curvature drives an AO gradient in the valleys */
  float ao = mix(0.55, 1.0, prof * 0.8 + 0.2);

  /* WAS vec2(120,25) x 4 = a 960-cycle top band at 1024. Brought inside Nyquist so the
     rolled mill finish actually reaches the frame instead of averaging to flat paint. */
  float mn = tfbm2(uv, vec2(34.0, 10.0), 3, 0.5, sd + 3.0);
  vec3 alb = uC[0] * (0.92 + mn * 0.16);
  /* galvanised sheet is a sharp reflector; 0.42 was reading as painted board */
  float rough = 0.30 + mn * 0.14;
  float metal = 1.0;

  /* galvanised spangle crystals — big, faceted, each catching the sky differently.
     This is the single cue that says "zinc" rather than "grey stripes". */
  vec3 sp = tvor(uv, 26.0, sd + 7.0);
  float spangle = ss(0.44, 0.05, sp.x);
  alb *= 1.0 + (h11(sp.z * 23.0) - 0.45) * 0.34 * spangle;
  rough += (h11(sp.z * 11.0) - 0.5) * 0.30 * spangle;
  h += (h11(sp.z * 47.0) - 0.5) * 0.018 * spangle;
  /* long roll-forming scratches down the length of the sheet */
  float roll = ss(0.05, 0.0, tvorA(uv, vec2(4.0, 90.0), sd + 9.0).x);
  rough += roll * 0.16;
  h -= roll * 0.004;

  /* faded paint on the ridges */
  if (paintAmt > 0.001){
    float pw = tfbm2(uv, vec2(7.0), 5, 0.55, sd + 11.0) * 0.5 + 0.5;
    float cov = ss(0.16, 0.40, pw + prof * 0.05) * paintAmt;
    alb = mix(alb, uC[2] * (0.85 + pw * 0.3), cov);
    rough = mix(rough, 0.66, cov);
    metal = mix(metal, 0.0, cov);
  }

  /* rust: concentrates in the valleys where water sits, and streaks down */
  float rn = tfbm2(uv, vec2(16.0), 5, 0.5, sd + 21.0) * 0.5 + 0.5;
  float valley = 1.0 - prof;
  float run = streaks(uv, 34.0, 1.6, sd + 23.0);
  float rust = ss(0.52, 0.94, rn * 0.60 + valley * 0.18 + run * 0.42) * rustAmt;
  alb = mix(alb, uC[1] * (0.55 + rn * 0.85), rust);
  rough = mix(rough, 0.90 + rn * 0.08, rust);
  metal *= 1.0 - sat(rust * 1.4);
  h += rust * 0.010;

  /* perforations / hail dents */
  if (dentAmt > 0.001){
    vec3 wd = tvor(uv, 22.0, sd + 31.0);
    float dent = ss(0.34, 0.06, wd.x) * step(0.66, wd.z);
    h -= dent * 0.05 * dentAmt;
    ao *= 1.0 - dent * 0.3 * dentAmt;
  }

  /* fastener screws along a horizontal line */
  vec2 fp = fract(uv * vec2(ribs, 3.0)) - vec2(0.5, 0.5);
  float onRidge = ss(0.5, 0.9, prof);
  float screw = ss(0.13, 0.05, length(fp * vec2(1.0, ribs / 3.0))) * onRidge;
  h += screw * 0.045;
  alb = mix(alb, vec3(0.30, 0.30, 0.31), screw * 0.6);
  rough = mix(rough, 0.35, screw * 0.6);
  metal = mix(metal, 1.0, screw * 0.7);

  alb *= 1.0 - (1.0 - ao) * 0.25;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.1, 1.0);
  s.metal  = sat(metal);
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * WOOD — planks, crates, plywood, painted
 * ===================================================================== */
wood: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float planks   = uP[0].x;    // planks across the tile (0 = single board)
  float ringF    = uP[0].y;
  float knotAmt  = uP[0].z;
  float varnish  = uP[0].w;
  float paintAmt = uP[1].x;
  float wearAmt  = uP[1].y;
  float plywoodAmt = uP[1].z;
  float gapDepth = uP[1].w;

  /* --- board layout: grain runs along +X -------------------------------- */
  float pIdx = 0.0, pv = 0.5, gap = 0.0, endGap = 0.0;
  vec2 guv = uv;
  if (planks > 0.5){
    float f = uv.y * planks;
    pIdx = floor(f);
    pv = fract(f);
    float pid = h21(vec2(pIdx, 3.7) + sd);
    /* every board gets its own grain offset, scale and tone */
    guv = vec2(uv.x + pid * 3.17, uv.y);
    gap = ss(0.030, 0.0, min(pv, 1.0 - pv));
    /* butt joints every couple of tiles */
    float segs = 2.0;
    float sx = fract(uv.x * segs + pid * 0.7);
    endGap = ss(0.012, 0.0, min(sx, 1.0 - sx));
  }
  float pid = h21(vec2(pIdx, 3.7) + sd);

  /* --- grain: rings distorted by low-frequency flow --------------------- */
  float warp1 = tfbm2(guv, vec2(2.0, 5.0), 4, 0.55, sd + 3.0);
  float warp2 = tfbm2(guv, vec2(9.0, 26.0), 4, 0.5, sd + 5.0);
  float axis = guv.y * (1.0 + pid * 0.4) + warp1 * 0.34 + warp2 * 0.060;
  /* ring spacing wanders — evenly spaced rings read as a contour map, not timber */
  float spacing = ringF * (0.75 + pid * 0.55) * (1.0 + tfbm2(guv, vec2(1.0, 3.0), 3, 0.5, sd + 15.0) * 0.35);
  float rings = fract(axis * spacing);
  float band = abs(sin(rings * 3.14159));
  float ring = 1.0 - pow(band, 0.35);

  /* Fine fibre streaks ALONG the grain.
   *
   * These two had their frequency axes the wrong way round, and that is the whole of
   * mat_closeup_wood's vertical smearing. Grain runs along +X here (the ring axis is
   * guv.y, planks stack along y), so a feature that runs along the grain must be LOW
   * frequency in u and HIGH frequency in v. They were the opposite: tvorA(300, 9)
   * builds cells 1/300 wide and 1/9 tall — isotropic distance in cell space, so each
   * "pore" came out as a sliver 33x longer across the boards than along them. On a
   * 2.4 m tile that is a 2 mm x 8 cm vertical dash, repeated at 300 per tile: a comb.
   * Ditto tfbm2(240, 14). Swapped, and the top octave brought inside the map's
   * Nyquist (fbm doubles per octave: 170 x 8 = 1360 was 0.75 texels at 1024).
   */
  float fibre = tfbm2(guv, vec2(9.0, 90.0), 4, 0.5, sd + 7.0);
  float pore = tvorA(guv, vec2(8.0, 150.0), sd + 9.0).x;
  float poreLine = ss(0.30, 0.0, pore);

  vec3 light = uC[0];
  vec3 dark  = uC[1];
  /* Grain contrast. The figure was carrying 0.62 of the light/dark range through a
   * smoothstep-free saturate and only 0.020 of height — so once the self-detail layer
   * was on top of it the panel read as a rendered wall with a faint streak rather than
   * as timber. The ring is now the dominant term in BOTH albedo and relief, which is
   * what makes a board look like a board at 34 cm. */
  float figure = sat(ring * 0.86 + fibre * 0.30 + 0.06);
  figure = figure * figure * (3.0 - 2.0 * figure);
  vec3 alb = mix(light, dark, figure);
  alb *= 1.0 + (pid - 0.5) * 0.22;
  alb *= 1.0 - poreLine * 0.30;

  /* Relief stays modest: real sawn timber carries its figure almost entirely in the
   * albedo, with sub-millimetre relief. Pushed further than this it reads as
   * sandblasted driftwood rather than a board. */
  float h = 0.66 - ring * 0.030 - poreLine * 0.024 + fibre * 0.014;
  float rough = 0.70 + ring * 0.14 + poreLine * 0.12 - fibre * 0.06;
  float ao = 1.0 - poreLine * 0.28;

  /* --- knots ------------------------------------------------------------ */
  if (knotAmt > 0.001){
    vec3 wk = tvor(guv, 5.0, sd + 11.0);
    float sel = step(0.80, wk.z);
    float d = wk.x * (1.0 + tfbm2(guv, vec2(30.0), 3, 0.5, sd + 13.0) * 0.25);
    float core = ss(0.22, 0.04, d) * sel;
    float halo = ss(0.44, 0.10, d) * sel;
    /* rings bunch up around the knot */
    float kr = fract(d * 26.0);
    float krl = 1.0 - pow(abs(sin(kr * 3.14159)), 0.6);
    alb = mix(alb, dark * 0.42, halo * knotAmt * 0.75);
    alb = mix(alb, dark * (0.22 + krl * 0.4), core * knotAmt);
    h -= core * 0.030 * knotAmt;
    rough += core * 0.10 * knotAmt;
    ao *= 1.0 - core * 0.35 * knotAmt;
  }

  /* --- plywood: alternating veneer with visible end grain --------------- */
  if (plywoodAmt > 0.001){
    float veneer = tfbm2(uv, vec2(12.0, 110.0), 4, 0.5, sd + 21.0);
    alb *= 1.0 + veneer * 0.10 * plywoodAmt;
    /* patched voids typical of construction ply */
    vec3 wv = tvor(uv, 9.0, sd + 23.0);
    float ptch = ss(0.24, 0.14, wv.x) * step(0.85, wv.z) * plywoodAmt;
    alb = mix(alb, light * 0.82, ptch);
    h -= ptch * 0.010;
    /* glue-line stripes */
    float gl = ss(0.9, 1.0, abs(sin(uv.y * TAU * 6.0)));
    alb = mix(alb, dark * 0.5, gl * 0.10 * plywoodAmt);
  }

  /* --- board gaps and end joints ---------------------------------------- */
  h -= (gap * 0.9 + endGap * 0.7) * gapDepth;
  ao *= 1.0 - (gap * 0.9 + endGap * 0.7) * 0.65;
  alb *= 1.0 - (gap * 0.6 + endGap * 0.5) * 0.55;
  /* chamfered, worn board edges catch light */
  float edgeUp = ss(0.10, 0.035, min(pv, 1.0 - pv)) * (1.0 - gap);
  alb *= 1.0 + edgeUp * 0.10;
  rough += edgeUp * 0.06;

  /* --- nails ------------------------------------------------------------ */
  if (planks > 0.5 && uP[2].x > 0.001){
    float nx = fract(uv.x * 2.0 + pid * 0.83);
    float ny = pv;
    float nd = length(vec2(nx - 0.5, (ny - 0.22) * 1.4));
    float present = step(0.45, h11(floor(uv.x * 2.0 + pid * 0.83) * 7.3 + pIdx * 13.1 + sd));
    float nail = ss(0.030, 0.014, nd) * present * uP[2].x;
    alb = mix(alb, vec3(0.055, 0.050, 0.046), nail * 0.9);
    rough = mix(rough, 0.62, nail * 0.85);
    h -= nail * 0.018;
    float bleed = ss(0.085, 0.028, nd) * present * uP[2].x;
    alb = mix(alb, vec3(0.085, 0.042, 0.018), (bleed - nail) * 0.5);
  }

  /* --- paint ------------------------------------------------------------ */
  if (paintAmt > 0.001){
    float pw = tfbm2(uv, vec2(6.0), 5, 0.58, sd + 31.0) * 0.5 + 0.5;
    float pe = tridge(uv, vec2(20.0), 4, 0.5, sd + 33.0);
    /* paint flakes off along the grain */
    float grainBias = ss(0.35, 0.75, ring);
    float cov = ss(0.32, 0.46, pw * 0.8 + pe * 0.3 - grainBias * 0.12);
    cov = mix(1.0, cov, paintAmt);
    float pn = tfbm2(uv, vec2(50.0), 4, 0.5, sd + 35.0);
    alb = mix(alb, uC[2] * (1.0 + pn * 0.10), cov);
    rough = mix(rough, 0.50 + pn * 0.10, cov);
    /* brush strokes */
    /* brush strokes follow the grain too — same swapped axis as fibre above */
    float stroke = tfbm2(uv, vec2(8.0, 110.0), 3, 0.5, sd + 37.0);
    h = mix(h, 0.70 + stroke * 0.006, cov * 0.8);
    float lip = ss(0.46, 0.38, pw * 0.8 + pe * 0.3) * ss(0.30, 0.38, pw * 0.8 + pe * 0.3);
    h += lip * 0.014;
    ao *= 1.0 - lip * 0.3;
  }

  /* --- varnish / sheen variance ----------------------------------------- */
  if (varnish > 0.001){
    float v = tfbm2(uv, vec2(5.0), 4, 0.5, sd + 41.0) * 0.5 + 0.5;
    float v2 = tfbm2(uv, vec2(28.0), 3, 0.5, sd + 43.0) * 0.5 + 0.5;
    float coat = ss(0.18, 0.62, v * 0.75 + v2 * 0.35) * varnish;
    rough = mix(rough, 0.20 + v2 * 0.18, coat);
    alb *= 1.0 + coat * 0.06;
    h = mix(h, h * 0.6 + 0.28, coat * 0.4);
  }

  /* --- wear: scuffs, splinters, dirt ------------------------------------ */
  if (wearAmt > 0.001){
    /* scuffs run along the boards, not across them */
    float sc = tvorA(uv, vec2(10.0, 130.0), sd + 51.0).x;
    float scr = ss(0.06, 0.0, sc) * wearAmt;
    alb *= 1.0 - scr * 0.25;
    rough += scr * 0.14;
    h -= scr * 0.006;
    float dirt = ss(0.45, 0.9, tfbm2(uv, vec2(4.0), 4, 0.5, sd + 53.0) * 0.5 + 0.5) * wearAmt;
    alb = mix(alb, uC[3], dirt * 0.4);
    rough += dirt * 0.06;
  }

  float cav = ss(0.66, 0.48, h);
  alb *= 1.0 - cav * 0.22;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.06, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * FABRIC — tarp, canvas, sandbag hessian, camo cloth, nylon webbing
 * ===================================================================== */
fabric: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  vec2 weaveF   = uP[0].xy;    // threads per tile, warp / weft
  float thick   = uP[0].z;     // relief depth
  float fuzz    = uP[0].w;
  float camoAmt = uP[1].x;
  float dirtAmt = uP[1].y;
  float ribAmt  = uP[1].z;     // webbing rib structure
  float coated  = uP[1].w;     // PVC coated tarp -> smoother, glossier

  /* --- plain weave: two interleaved thread families --------------------- */
  vec2 tp = uv * weaveF;
  vec2 cell = floor(tp);
  vec2 f = fract(tp);
  float check = mod(cell.x + cell.y, 2.0);

  /* thread cross-sections (cosine tubes) */
  float warp = pow(sin(f.x * 3.14159), 0.7);      // vertical threads
  float weft = pow(sin(f.y * 3.14159), 0.7);      // horizontal threads
  float over = check;                              // which one is on top
  float hWarp = warp * (0.55 + over * 0.45);
  float hWeft = weft * (1.0 - over * 0.45) * 0.9;
  float h = 0.40 + max(hWarp, hWeft) * thick;

  /* thread twist: fine helical fibres along each yarn */
  float twistW = sin((f.y + cell.x * 0.37) * TAU * 5.0) * 0.5 + 0.5;
  float twistF = sin((f.x + cell.y * 0.53) * TAU * 5.0) * 0.5 + 0.5;
  float twist = mix(twistF, twistW, step(hWeft, hWarp));
  h += (twist - 0.5) * 0.045 * thick;

  float ao = mix(0.55, 1.0, ss(0.0, 0.75, max(hWarp, hWeft)));

  /* yarn-to-yarn tonal variation */
  float yv = h21(cell * 1.13 + sd) * 0.5 + h21(vec2(mix(cell.y, cell.x, step(hWeft, hWarp)), 7.1) + sd) * 0.5;
  vec3 alb = uC[0] * (0.82 + yv * 0.36);
  float rough = 0.86 + (1.0 - twist) * 0.06;

  /* --- webbing ribs ----------------------------------------------------- */
  if (ribAmt > 0.001){
    float rib = abs(fract(uv.y * 9.0) - 0.5) * 2.0;
    float r = ss(0.75, 0.25, rib);
    h += (r - 0.4) * 0.10 * ribAmt;
    ao *= mix(1.0, 0.8 + r * 0.2, ribAmt);
    /* stitch line */
    float st = ss(0.06, 0.0, abs(fract(uv.y * 9.0 + 0.5) - 0.5)) * ss(0.4, 0.6, fract(uv.x * 60.0));
    alb = mix(alb, uC[3], st * 0.5 * ribAmt);
    h += st * 0.03 * ribAmt;
  }

  /* --- camo pattern ------------------------------------------------------ */
  if (camoAmt > 0.001){
    float n1 = tfbm2(uv, vec2(3.4), 4, 0.6, sd + 11.0) * 0.5 + 0.5;
    float n2 = tfbm2(uv, vec2(6.1), 4, 0.55, sd + 13.0) * 0.5 + 0.5;
    float n3 = tfbm2(uv, vec2(11.0), 3, 0.5, sd + 17.0) * 0.5 + 0.5;
    /* blobby, hard-edged patches like a printed multicam */
    float m1 = ss(0.46, 0.52, n1);
    float m2 = ss(0.52, 0.57, n2 * 0.8 + n3 * 0.3);
    float m3 = ss(0.60, 0.64, n3);
    vec3 camo = uC[1];
    camo = mix(camo, uC[2], m1);
    camo = mix(camo, uC[3], m2 * 0.85);
    camo = mix(camo, uC[1] * 0.45, m3 * 0.5);
    /* print does not perfectly cover the yarn tops */
    float printCov = 0.82 + 0.18 * ss(0.2, 0.8, max(hWarp, hWeft));
    alb = mix(alb, alb * 0.35 + camo * (0.8 + yv * 0.3), camoAmt * printCov);
  }

  /* --- PVC coating ------------------------------------------------------- */
  if (coated > 0.001){
    float cn = tfbm2(uv, vec2(30.0), 4, 0.5, sd + 21.0);
    rough = mix(rough, 0.40 + cn * 0.14, coated);
    h = mix(h, h * 0.45 + 0.42 + cn * 0.006, coated * 0.75);
    alb = mix(alb, alb * 1.06, coated * 0.5);
    /* creases and fold lines */
    float cr = tridge(uv, vec2(3.0), 4, 0.5, sd + 23.0);
    float crease = ss(0.72, 0.97, cr) * coated;
    h -= crease * 0.05;
    ao *= 1.0 - crease * 0.35;
    alb *= 1.0 - crease * 0.15;
    rough += crease * 0.14;
  }

  /* --- folds and crumple ------------------------------------------------ */
  float wrinkle = uP[2].x;
  if (wrinkle > 0.001){
    float fold = tfbm2(uv, vec2(2.4, 1.7), 4, 0.55, sd + 51.0);
    float crum = tridge(uv, vec2(7.0), 4, 0.5, sd + 53.0);
    h  += fold * 0.13 * wrinkle - ss(0.55, 0.95, crum) * 0.05 * wrinkle;
    ao *= 1.0 - ss(0.50, 0.95, crum) * 0.30 * wrinkle;
    alb *= 1.0 + fold * 0.13 * wrinkle;
    rough += ss(0.6, 0.98, crum) * 0.05 * wrinkle;
    /* sun-bleached tops of the folds */
    alb *= 1.0 + ss(0.2, 0.9, fold * 0.5 + 0.5) * 0.10 * wrinkle;
  }

  /* --- loose fibres / fuzz ---------------------------------------------- */
  if (fuzz > 0.001){
    /* loose fibres lie ALONG the yarn, and 3x the weave frequency was 1.6 texels per
       cell — noise, not fuzz */
    float fz = tvorA(uv, vec2(weaveF.x * 0.35, weaveF.y * 1.1), sd + 31.0).x;
    float hair = ss(0.16, 0.0, fz);
    h += hair * 0.020 * fuzz;
    alb *= 1.0 + hair * 0.14 * fuzz;
    rough += hair * 0.05 * fuzz;
    float lint = ss(0.55, 0.95, tfbm2(uv, vec2(80.0), 3, 0.5, sd + 33.0) * 0.5 + 0.5);
    alb *= 1.0 + lint * 0.10 * fuzz;
  }

  /* --- wear: abrasion at the peaks, dirt in the valleys ----------------- */
  float abr = ss(0.5, 0.9, tfbm2(uv, vec2(7.0), 4, 0.5, sd + 41.0) * 0.5 + 0.5);
  alb *= 1.0 - abr * 0.10;
  rough += abr * 0.05;

  float cav = ss(0.62, 0.40, h);
  float dirt = sat(cav * 1.1 + ss(0.5, 0.95, tfbm2(uv, vec2(4.0), 4, 0.5, sd + 43.0) * 0.5 + 0.5) * 0.7) * dirtAmt;
  alb = mix(alb, uC[3] * 0.55, dirt * 0.55);
  rough += dirt * 0.05;
  ao *= 1.0 - cav * 0.30;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.15, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * SANDBAG — hessian weave stretched over a bulging fill
 * ===================================================================== */
sandbag: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  vec2 weaveF = uP[0].xy;
  float thick = uP[0].z;
  float dirtAmt = uP[0].w;

  /* coarse jute weave */
  vec2 tp = uv * weaveF;
  vec2 cell = floor(tp), f = fract(tp);
  float check = mod(cell.x + cell.y, 2.0);
  float warp = pow(sin(f.x * 3.14159), 0.6);
  float weft = pow(sin(f.y * 3.14159), 0.6);
  float hWarp = warp * (0.5 + check * 0.5);
  float hWeft = weft * (1.0 - check * 0.5);
  float top = max(hWarp, hWeft);

  /* the fill pushes the weave out unevenly */
  float bulge = tfbm2(uv, vec2(3.0), 4, 0.55, sd + 3.0);
  float lumps = tfbm2(uv, vec2(11.0), 4, 0.5, sd + 5.0);

  float h = 0.42 + top * thick + bulge * 0.11 + lumps * 0.035;
  /* Weave AO contrast. At 190 threads per tile the crossings are sub-pixel from ~8 m,
     and a 0.5-to-1.0 AO swing at that frequency shimmers into a bright wire mesh. */
  float ao = mix(0.72, 1.0, ss(0.0, 0.8, top)) * (0.88 + bulge * 0.14);

  /* Per-crossing tonal jitter. At 0.72 + 0.5 this alternated light and dark cell by
     cell and, on top of a 6 mm weave, that checkerboard is exactly what read as wicker
     basketry. Sacking yarn is one batch of jute: it varies, but by a quarter of that. */
  float yv = h21(cell * 1.7 + sd);
  vec3 alb = uC[0] * (0.88 + yv * 0.22) * (1.0 + bulge * 0.10);
  float rough = 0.92;

  /* jute fibre hairs, running along the yarn */
  float fz = tvorA(uv, vec2(weaveF.x * 0.30, weaveF.y * 1.0), sd + 11.0).x;
  float hair = ss(0.18, 0.0, fz);
  h += hair * 0.016;
  alb *= 1.0 + hair * 0.10;
  /* fibre-scale value noise: below the weave, so the cloth never reads as flat yarn */
  float fibreN = tfbm2(uv, vec2(weaveF.x * 0.55), 2, 0.5, sd + 13.0);
  alb *= 1.0 + fibreN * 0.16;
  rough += fibreN * 0.05;

  /* stitching along the top seam */
  float seam = ss(0.045, 0.0, abs(uv.y - 0.06));
  float stitch = seam * ss(0.35, 0.5, fract(uv.x * 40.0));
  h += stitch * 0.03;
  alb = mix(alb, uC[1], stitch * 0.6);

  /* sand and mud staining, sun bleaching at the top */
  float bleach = ss(0.9, 0.1, uv.y);
  alb *= 1.0 + bleach * 0.14;
  float cav = ss(0.60, 0.34, h);
  float dirt = sat(cav * 1.2 + ss(0.45, 0.95, tfbm2(uv, vec2(5.0), 4, 0.5, sd + 21.0) * 0.5 + 0.5)) * dirtAmt;
  alb = mix(alb, uC[2], dirt * 0.55);
  rough += dirt * 0.04;
  ao *= 1.0 - cav * 0.35;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.3, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * GLASS — clean and grimy
 * ===================================================================== */
glass: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float grime = uP[0].x;
  float dust  = uP[0].y;
  float crack = uP[0].z;
  float streak = uP[0].w;

  float h = 0.5;
  vec3 alb = uC[0];
  float rough = 0.02;
  float ao = 1.0;

  /* rolled-glass waviness — extremely subtle, but it is what sells glass */
  float wav = tfbm2(uv, vec2(6.0), 3, 0.5, sd + 3.0);
  h += wav * 0.010;
  rough += abs(wav) * 0.010;

  /* dried run-off streaks */
  float run = streaks(uv, 22.0, 1.5, sd + 11.0);
  float rn = sat(run) * streak;
  rough = mix(rough, 0.24, rn * 0.6);
  alb = mix(alb, uC[1], rn * 0.20);
  h += rn * 0.004;

  /* dust film, denser at the bottom and in the corners */
  float dn = tfbm2(uv, vec2(14.0), 4, 0.5, sd + 21.0) * 0.5 + 0.5;
  float grad = ss(0.0, 0.75, uv.y);
  float df = sat(dn * 0.7 + grad * 0.5) * dust;
  rough = mix(rough, 0.42, df * 0.75);
  alb = mix(alb, uC[1], df * 0.45);

  /* greasy fingerprint / splatter grime */
  float gn = tturb(uv, vec2(9.0), 4, 0.55, sd + 31.0);
  float gm = ss(0.22, 0.72, gn) * grime;
  rough = mix(rough, 0.62, gm * 0.9);
  alb = mix(alb, uC[2] * 0.75, gm * 0.85);
  h += gm * 0.006;

  /* fine spatter dots */
  vec3 wd = tvor(uv, 55.0, sd + 41.0);
  float dot = ss(0.10, 0.02, wd.x) * step(0.6, wd.z) * grime;
  rough = mix(rough, 0.6, dot);
  alb = mix(alb, uC[2] * 1.2, dot * 0.7);

  /* impact cracks */
  if (crack > 0.001){
    float c = crackNet(uv, vec2(6.0), 0.93, sd + 51.0);
    float c2 = crackNet(uv, vec2(17.0), 0.955, sd + 53.0);
    float cr = sat(c + c2 * 0.7) * crack;
    h -= cr * 0.06;
    rough = mix(rough, 0.5, cr);
    alb = mix(alb, vec3(0.55), cr * 0.5);
    ao *= 1.0 - cr * 0.3;
  }

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.005, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * RUBBER / POLYMER — tyres, grips, gun furniture, plastic crates
 * ===================================================================== */
polymer: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float pebbleAmt = uP[0].x;   // moulded stipple / pebble grain
  float pebbleF   = uP[0].y;
  float rBase     = uP[0].z;
  float checkerAmt= uP[0].w;   // grip checkering
  float ribAmt    = uP[1].x;   // moulded ribs
  float scuffAmt  = uP[1].y;
  float glossVar  = uP[1].z;
  float mouldLine = uP[1].w;

  float n1 = tfbm2(uv, vec2(9.0), 4, 0.5, sd + 3.0);
  float n2 = tfbm2(uv, vec2(60.0), 3, 0.5, sd + 5.0);

  vec3 alb = uC[0] * (1.0 + n1 * 0.06 + n2 * 0.035);
  float h = 0.62 + n1 * 0.006;
  float rough = rBase + n2 * 0.05;
  float ao = 1.0;

  /* injection-moulded pebble/stipple texture — the signature of polymer */
  if (pebbleAmt > 0.001){
    vec3 wp = tvor(uv, pebbleF, sd + 11.0);
    float bump = ss(0.5, 0.02, wp.x);
    float top = pow(bump, 0.6);
    h += top * 0.030 * pebbleAmt;
    ao *= 1.0 - (1.0 - bump) * 0.22 * pebbleAmt;
    rough += (0.5 - top) * 0.10 * pebbleAmt;
    alb *= 1.0 + (top - 0.5) * 0.08 * pebbleAmt;
    /* second finer stipple */
    vec3 wp2 = tvor(uv, pebbleF * 2.7, sd + 13.0);
    h += ss(0.5, 0.05, wp2.x) * 0.010 * pebbleAmt;
  }

  /* diamond checkering */
  if (checkerAmt > 0.001){
    vec2 r = vec2(uv.x + uv.y, uv.x - uv.y) * 26.0;
    vec2 c = abs(fract(r) - 0.5) * 2.0;
    float dia = ss(0.95, 0.15, max(c.x, c.y));
    float region = ss(0.35, 0.45, tfbm2(uv, vec2(2.5), 3, 0.5, sd + 21.0) * 0.5 + 0.5);
    float k = dia * checkerAmt * mix(0.25, 1.0, region);
    h += k * 0.045;
    ao *= 1.0 - (1.0 - dia) * 0.25 * checkerAmt;
    rough += (0.5 - dia) * 0.10 * checkerAmt;
  }

  /* moulded ribs */
  if (ribAmt > 0.001){
    float ribWob = tfbm2(uv, vec2(3.0, 6.0), 3, 0.5, sd + 41.0) * 0.05;
    float rib = abs(fract(uv.x * 9.0 + ribWob) - 0.5) * 2.0;
    float r = ss(0.80, 0.34, rib);
    float ribMask = ss(0.35, 0.55, tfbm2(uv, vec2(2.0), 3, 0.5, sd + 43.0) * 0.5 + 0.5);
    h += (r - 0.5) * 0.055 * ribAmt * ribMask;
    ao *= 1.0 - (1.0 - r) * 0.22 * ribAmt * ribMask;
  }

  /* mould parting line + sprue witness marks */
  if (mouldLine > 0.001){
    float d = abs(uv.y - 0.5);
    float line = ss(0.006, 0.0, d);
    h += line * 0.012 * mouldLine;
    rough += line * 0.10 * mouldLine;
    alb *= 1.0 + line * 0.08 * mouldLine;
  }

  /* gloss variation — real plastic is never uniformly rough */
  float gv = tfbm2(uv, vec2(4.0), 4, 0.5, sd + 31.0) * 0.5 + 0.5;
  rough += (gv - 0.5) * 0.16 * glossVar;

  /* scuffs and abrasion on the high points */
  if (scuffAmt > 0.001){
    float sc = tvorA(uv, vec2(120.0, 20.0), sd + 41.0).x;
    float scr = ss(0.07, 0.0, sc) * scuffAmt;
    float peak = ss(0.60, 0.72, h);
    float k = scr * mix(0.4, 1.0, peak);
    alb = mix(alb, alb * 1.45 + vec3(0.02), k * 0.7);
    rough = mix(rough, 0.68, k * 0.8);
    h -= k * 0.004;
  }

  /* GLASS-FILLED NYLON. uP[2].x.
   *
   * The reviewer's complaint about the weapon is that a polymer handguard and an
   * anodised receiver measure 3 degrees of hue and one finish apart when they are not
   * even the same class of material. A 30 % glass-filled nylon does something no metal
   * does: the chopped fibres sit just under the moulded skin and scatter, so the
   * surface carries a fine pale fleck AND a slightly milky, low-contrast specular that
   * is duller than the anodising next to it. That difference is the whole point. */
  float fillAmt = uP[2].x;
  if (fillAmt > 0.001){
    vec3 wf = tvorA(uv, vec2(150.0, 42.0), sd + 71.0);
    float fibre = ss(0.055, 0.0, wf.x);
    alb = mix(alb, alb * 1.9 + vec3(0.012), fibre * 0.55 * fillAmt);
    rough += fibre * 0.13 * fillAmt;
    float haze = tfbm2(uv, vec2(26.0), 3, 0.5, sd + 73.0) * 0.5 + 0.5;
    rough += (haze - 0.5) * 0.10 * fillAmt;
  }

  /* SOFT-TOUCH / VULCANISED RUBBER. uP[2].y — no gloss anywhere, and the stipple is
   * rounded rather than crisp because the material deforms under the mould. */
  float softAmt = uP[2].y;
  rough = mix(rough, max(rough, 0.86), softAmt);

  /* handling grime in the valleys */
  float cav = ss(0.63, 0.50, h);
  alb *= 1.0 - cav * 0.18;
  rough += cav * 0.05;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.10, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * CERAMIC TILE
 * ===================================================================== */
tile: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  vec2 counts = uP[0].xy;
  float groutW = uP[0].z;
  float chipAmt = uP[0].w;
  float dirtAmt = uP[1].x;
  float accentFrac = uP[1].y;    // fraction of tiles laid from a contrasting batch
  float valueSpread = uP[1].z;   // per-tile value variation within a batch

  Lat L = lattice(uv, counts, 0.0, counts.y / counts.x);
  float grout = ss(groutW, groutW * 0.35, L.border);

  /* Glazed body. A two-colour lerp between two greys four per cent apart is what made
   * this "a plain white grid": every tile in the field came out the same value, so the
   * only contrast in the material was the grout. A tiled floor in a Levantine market
   * town is laid from mixed batches — mostly one glaze, a scattering of a contrasting
   * one, and a real value spread inside each batch from kiln position. */
  float n = tfbm2(uv, vec2(40.0), 4, 0.5, sd + L.id * 33.0);
  float accent = step(1.0 - accentFrac, h11(L.id * 71.3 + sd * 0.31));
  vec3 batch = mix(mix(uC[0], uC[1], sat(L.id * 1.4 - 0.2)), uC[2], accent);
  batch *= 1.0 - (h11(L.id * 37.9 + 5.0) - 0.5) * valueSpread;
  /* glaze pooling: thicker at the tile edge, thinner over the crown */
  vec2 gc = L.luv - 0.5;
  float pool = ss(0.05, 0.24, max(abs(gc.x), abs(gc.y)));
  vec3 tcol = batch * (1.0 + n * 0.09) * (1.0 - pool * 0.10);
  float craze = crackNet(uv, vec2(28.0), 0.965, sd + 3.0);
  tcol *= 1.0 - craze * 0.18;

  float tr = 0.26 + n * 0.08 + craze * 0.28;
  float th = 0.78 + n * 0.004 - craze * 0.006;

  /* slight dish in the middle of each tile (kiln warp) */
  vec2 c = L.luv - 0.5;
  th -= (1.0 - dot(c, c) * 3.2) * 0.008;

  /* chipped glaze reveals matte biscuit */
  vec3 wc = tvor(uv, 70.0, sd + 11.0);
  float chip = ss(0.20, 0.05, wc.x) * step(0.80, wc.z) * ss(0.28, 0.06, L.border) * chipAmt;
  tcol = mix(tcol, batch * vec3(1.24, 1.14, 1.02), chip);   // raw biscuit under the glaze
  tr = mix(tr, 0.85, chip);
  th -= chip * 0.02;

  /* grout */
  float gn = tfbm2(uv, vec2(90.0), 4, 0.5, sd + 21.0);
  vec3 gcol = uC[3] * (1.0 + gn * 0.22);
  float gr = 0.90 + gn * 0.06;
  float gh = 0.60 + gn * 0.010;

  vec3 alb = mix(tcol, gcol, grout);
  float rough = mix(tr, gr, grout);
  float h = mix(th, gh, grout);
  float ao = 1.0 - grout * 0.45;

  /* dirt loves grout lines */
  float dirt = sat(grout * 1.1 + ss(0.55, 0.95, tfbm2(uv, vec2(6.0), 4, 0.5, sd + 31.0) * 0.5 + 0.5) * 0.6) * dirtAmt;
  alb *= 1.0 - dirt * 0.35;
  rough = mix(rough, 0.9, dirt * 0.5);

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.04, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * FOLIAGE — an alpha-cut leaf cluster atlas on one tile
 * ===================================================================== */
foliage: /* glsl */`
/* signed distance to a leaf shape centred at 0, pointing +Y, length L, width W */
float leafSDF(vec2 p, float L, float W){
  float t = clamp(p.y / L, -0.15, 1.0);
  float w = W * pow(sin(clamp(t, 0.0, 1.0) * 3.14159), 0.55) * (1.0 - 0.25 * t);
  return abs(p.x) - w;
}

void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float leaves = uP[0].x;
  float dry    = uP[0].y;

  float cover = 0.0;
  float vein = 0.0;
  float midrib = 0.0;
  float depth = 0.0;
  float lid = 0.0;
  float leafT = 0.0;

  /* Scatter leaves over a jittered grid; keep it tileable by wrapping the grid.
   *
   * WAS a 5x5 grid of 11-18 cm blades 2-4 cm wide over a 70 cm tile — about 13 % of the
   * map covered, so the cut-out was mostly hole and the material rendered, in the
   * reviewer's words, "as an alpha card with large holes, broken alpha, not foliage".
   * It was not broken; there was simply not enough plant in it. A 7x7 grid of wider,
   * longer blades takes coverage past 50 %, which is what a tuft of scrub actually
   * blocks, and the second thinner layer below fills the interstices so the silhouette
   * has fine edges instead of 25 isolated ellipses. */
  const int N = 7;
  for (int gy = 0; gy < N; gy++){
    for (int gx = 0; gx < N; gx++){
      vec2 cell = vec2(float(gx), float(gy));
      vec3 rnd = h23(cell + sd * 0.731);
      vec2 centre = (cell + 0.12 + rnd.xy * 0.76) / float(N);
      vec2 d = uv - centre;
      d -= round(d);                                  // toroidal wrap
      float ang = rnd.z * TAU;
      float ca = cos(ang), sa = sin(ang);
      vec2 p = vec2(ca * d.x - sa * d.y, sa * d.x + ca * d.y);
      float L = (0.19 + rnd.x * 0.14) * leaves;
      float W = (0.026 + rnd.y * 0.024) * leaves;
      /* leaf occupies 0..L along +Y */
      vec2 q = p + vec2(0.0, L * 0.35);
      float inside = -leafSDF(q, L, W);
      float onLeaf = ss(-0.004, 0.004, inside) * ss(-0.02, 0.005, q.y) * ss(L + 0.02, L - 0.02, q.y);
      if (onLeaf > cover){
        cover = onLeaf;
        lid = rnd.z;
        leafT = clamp(q.y / L, 0.0, 1.0);
        midrib = ss(0.010, 0.0, abs(q.x)) * onLeaf;
        float vy = q.y * 60.0 + abs(q.x) * 220.0;
        vein = (sin(vy) * 0.5 + 0.5) * ss(W * 1.2, 0.0, abs(q.x)) * onLeaf;
        depth = onLeaf * (0.4 + 0.6 * (1.0 - abs(q.x) / max(W, 1e-4)));
      }
    }
  }

  /* Dry scrub, not lawn.
   *
   *  - dryness is now per-blade AND biased along the blade, so a tuft contains
   *    green-ish bases with straw tips and a brown fraction of blades that are dead
   *    all the way down. A tuft of one colour is the tell that it is a texture.
   *  - the blade tip bleaches toward the dead colour instead of just getting brighter,
   *    which is what actually happens in sun.
   *  - the midrib no longer gets a 1.35x brightening; a raised rib catches light from
   *    the normal map, it does not need a paint-on highlight that survives into shadow.
   */
  float brownFrac = uP[0].z;
  float n = tfbm2(uv, vec2(40.0), 4, 0.5, sd + 3.0);
  vec3 green = mix(uC[0], uC[1], sat(lid * 1.2 - 0.1 + n * 0.25));
  vec3 dead  = uC[2] * (0.78 + 0.42 * h11(lid * 17.3 + 2.0));
  float deadBlade = step(1.0 - brownFrac, h11(lid * 53.1 + 7.0));
  float dryness = sat(dry * (0.28 + 0.85 * h11(lid * 41.0)) + leafT * 0.45 * dry);
  dryness = max(dryness, deadBlade * (0.72 + 0.28 * leafT));
  vec3 alb = mix(green, dead, dryness);
  /* dust: every roadside plant in a dry climate carries a film of it */
  alb = mix(alb, alb * 0.86 + vec3(0.055, 0.050, 0.038), 0.30 * dry);
  alb *= 0.86 + 0.22 * leafT;
  alb *= 1.0 + n * 0.12;
  alb = mix(alb, alb * 1.10, midrib * 0.5);
  alb *= 1.0 - vein * 0.12;

  float h = 0.45 + depth * 0.35 + midrib * 0.16 + vein * 0.04;
  float rough = 0.62 + n * 0.10 - midrib * 0.08 + dryness * 0.22;
  float ao = 1.0 - (1.0 - depth) * 0.25 * cover;

  s.albedo = max(alb, vec3(0.004));
  s.rough  = clamp(rough, 0.2, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  /* A second, finer layer of thin blades fills the gaps between the main leaves so the
   * cut-out edge reads as a plant silhouette rather than as 49 separate ellipses. It
   * contributes coverage and shading but no relief, so it costs one noise evaluation. */
  float fine = ss(0.030, 0.0, tvorA(uv, vec2(30.0, 9.0), sd + 61.0).x);
  float fine2 = ss(0.026, 0.0, tvorA(uv, vec2(9.0, 32.0), sd + 67.0).x);
  float filler = sat(max(fine, fine2 * 0.85)) * 0.80;
  s.albedo = mix(s.albedo, s.albedo * (0.80 + 0.30 * n), sat(filler - cover));
  s.alpha  = ss(0.26, 0.58, max(cover, filler));
}
`,

/* ===================================================================== *
 * SKIN
 * ===================================================================== */
skin: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;
  float poreAmt = uP[0].x;
  float stubble = uP[0].y;
  float blemish = uP[0].z;
  float oil     = uP[0].w;

  float zoning = uP[1].x;
  float flush  = uP[1].y;

  float m1 = tfbm2(uv, vec2(4.0), 4, 0.55, sd + 3.0);
  float m2 = tfbm2(uv, vec2(16.0), 4, 0.5, sd + 5.0);

  /* Dermal colour. Skin is not one hue: the cheeks, nose and ears run red because the
   * capillary bed is close to the surface, the forehead and jaw run sallow-yellow, and
   * the two zones overlap on a scale of a few centimetres. A single base colour with a
   * little noise on it is exactly the "flat salmon ball" this was.
   *
   * THE ZONING IS A HUE ROTATION AT CONSTANT CHROMA. That sentence is the whole fix.
   * The previous pass bought its hue travel by raising SATURATION — base at HSV 0.38,
   * capillary at 0.49, sallow at 0.53 — and a saturation-driven zone map makes the
   * brightest, most-lit part of a face the most saturated part of it, which is backwards
   * (real skin desaturates into the highlight) and it prints as blotches, because the eye
   * reads a chroma step as a stain long before it reads it as anatomy. The three zone
   * colours are now at IDENTICAL HSV saturation (0.30) and identical Rec.709 luma (133),
   * separated only in hue and only inside 11-41 degrees. See DEFS.skin. */
  float m3 = tfbm2(uv, vec2(48.0), 3, 0.5, sd + 7.0);
  /* 2.2 and 1.15 over a 30 cm tile is a 13 cm and a 26 cm blotch. On a head that is
   * ONE zone across the whole face, which is why "the nose is not redder than the
   * cheek" survived the previous pass: there was nothing for the two points to be on
   * opposite sides of. Real dermal zoning runs at 4-8 cm — nose and ears against
   * forehead and jaw — so 5.0 and 2.6 (6 cm and 11.5 cm) is the honest scale, and it
   * also puts several zones across the showcase sphere instead of half of one. */
  float m4 = tfbm2(uv, vec2(5.0), 3, 0.6, sd + 9.0);      // the coarse zone mask
  float m5 = tfbm2(uv, vec2(2.6), 2, 0.6, sd + 19.0);     // a second, larger zone axis
  vec3 alb = uC[0];

  /* FOUR dermal zones, not two, and mixed at nearly full strength.
   *
   * The previous pass had a red zone at 0.72 blend and a sallow zone at 0.58 between
   * colours only 23 degrees of hue apart, then multiplied a mottle over the top — which
   * comes out as 6 degrees of hue travel across a whole sphere. Real facial skin runs
   * roughly 10-15 deg on the cheeks, nose and ears (capillary bed near the surface),
   * 45-55 deg across the forehead and jaw (sallow, carotene, less vasculature), and
   * dips COOL and slightly violet through the beard mask and the thin skin under the
   * eye. Those are the three things a portrait painter mixes separately and they are
   * what the measurement is asking for.
   *
   * The two masks are deliberately at different frequencies (2.2 and 1.15 over a 30 cm
   * tile, i.e. ~14 cm and ~26 cm blotches) so the zones overlap irregularly instead of
   * producing one clean split you can read as a texture. */
  /* THE ZONE MASKS HAVE TO BE STRETCHED AND MUTUALLY EXCLUSIVE, and they were neither.
   *
   * tfbm2 is an averaged Perlin sum: its output sits in roughly +-0.4, so n*0.5+0.5
   * spans about 0.30..0.70 and clusters hard around 0.5. Feeding that into
   * ss(0.28, 0.88, z) and ss(0.72, 0.12, z) does two damaging things at once — neither
   * zone ever reaches full strength (the red one topped out near 0.78 and only on the
   * rarest texels), and BOTH evaluate to ~0.34 across the whole middle of the range, so
   * the commonest case is 34 % red mixed over 34 % sallow, which averages straight back
   * to the base colour. That is why the map was authored with 40 degrees of hue between
   * its zones and delivered 11 on screen: the zoning was cancelling itself out.
   *
   * Stretching z about its own midpoint fills 0..1, and splitting the two ramps at that
   * midpoint makes them mutually exclusive by construction: at z = 0.5 both are zero
   * and the base shows, above it the skin runs to the capillary red, below it to the
   * sallow carotene, and neither is ever diluted by the other. */
  float z1 = clamp((m4 * 0.5 + 0.5 - 0.5) * 3.2 + 0.5, 0.0, 1.0);
  float z2 = clamp((m5 * 0.5 + 0.5 - 0.5) * 3.0 + 0.5, 0.0, 1.0);
  /* Thresholds set so the three zones each own a real share of the surface. The noise
   * is bell-shaped even after stretching, so splitting exactly at the midpoint leaves
   * both extremes as a thin minority and area-weighted statistics never see them —
   * measured, that put the 95th percentile of hue back on the BASE colour and the
   * sallow zone outside the sample entirely. 0.46/0.54 gives roughly 37 % sallow,
   * 26 % base, 37 % capillary. */
  float redZone  = ss(0.50, 0.80, z1) * zoning;
  float sallow   = ss(0.50, 0.18, z1) * zoning;
  float coolZone = ss(0.54, 0.84, z2) * (1.0 - redZone) * zoning;
  alb = mix(alb, uC[1], redZone * 0.92);                          // capillary flush
  alb = mix(alb, uC[2], sallow * 0.92);                           // sallow / carotene
  /* Beard mask / eye socket. WAS uC[1] * vec3(0.60, 0.78, 1.02) — a tint that puts BLUE
   * above GREEN above RED, i.e. a slate-blue patch painted into a skin albedo. On a
   * cheek it is a bruise. What actually happens under thin beard-shadowed skin is a loss
   * of chroma and a small cool shift, not a change of hue family, so this is now a
   * desaturating multiplier that leaves R > G > B intact. */
  alb = mix(alb, uC[0] * vec3(0.90, 0.93, 0.99), coolZone * 0.55);

  /* The capillary network itself. A ridged noise net at ~3 cm reads as the vessel
   * structure under thin skin, and it is what stops the red zone being a flat blush.
   * BOTH flush terms are now gated by redZone — as global blankets they painted the
   * sallow zone red as well, which is the other half of the compression above.
   *
   * The vessel tint WAS uC[1] * vec3(1.22, 0.66, 0.62): that is HSV saturation 0.64 on a
   * material whose zones sit at 0.30, so the vessel net alone doubled the chroma of every
   * texel it touched and it is a large part of the measured 0.568 on the nose. A vessel
   * seen through 1-2 mm of dermis is redder than the surrounding tissue by a little, not
   * by a factor of two. */
  float vasc = ss(0.60, 0.98, tridge(uv, vec2(9.0), 4, 0.55, sd + 27.0));
  float flushK = flush * (0.20 + 0.80 * redZone);
  alb = mix(alb, uC[1] * vec3(1.07, 0.90, 0.87), vasc * 0.45 * flushK);
  alb = mix(alb, uC[1], ss(0.30, 0.95, m1 * 0.5 + 0.5) * 0.45 * flushK);

  /* fine mottling: the 2-6 mm blotching that reads as living tissue up close. Tinted,
   * not just valued — a per-blotch hue wobble is the difference between "skin" and
   * "one colour with noise on it". Amplitudes roughly halved: at the old +-0.10 on red
   * and -+0.09 on blue a texel sitting in the sallow zone could be pushed past G > R,
   * which is a GREEN skin texel, and that is precisely what printed on the jaw and the
   * neck of char_closeup. */
  alb *= 1.0 + m2 * 0.09 + m3 * 0.05;
  alb *= vec3(1.0 + m3 * 0.05, 1.0, 1.0 - m3 * 0.045);
  alb *= 1.0 - ss(0.55, 0.05, m1 * 0.5 + 0.5) * 0.13;

  float h = 0.62;
  float rough = 0.52 + m2 * 0.05;
  float ao = 1.0;

  /* Pores. WAS tvor(uv, 260) at 1024 — 3.9 texels per cell, so the pores aliased and
   * the mip chain then averaged them out of existence. At 2048 with 150 cells the tile
   * is 30 cm, i.e. a 2 mm pore pitch at 13.6 texels each: it survives. */
  vec3 wp = tvor(uv, 150.0, sd + 11.0);
  float pore = ss(0.34, 0.04, wp.x) * step(0.30, wp.z);
  float poreDepth = 0.5 + 0.5 * h11(wp.z * 31.0);
  /* Depth 0.034 -> 0.062. The character team scales this material's normalScale to 0.42
   * (a soldier at three metres does not want an architectural pore), so whatever depth
   * is authored here arrives on a face at well under half strength. The map has to hold
   * the relief that survives that, and 0.034 x 0.42 is nothing. */
  h -= pore * 0.062 * poreAmt * poreDepth;
  ao *= 1.0 - pore * 0.52 * poreAmt;
  rough += pore * 0.20 * poreAmt;
  /* Pores are not just darker, they are REDDER — you are looking down a shaft into the
   * dermis, so the light that comes back out of one has taken the long way. */
  alb *= 1.0 - pore * 0.16 * poreAmt * poreDepth;
  alb = mix(alb, uC[1] * 0.86, pore * 0.18 * poreAmt);

  /* fine skin micro-relief (the diamond cell pattern) */
  float c1 = tridge(uv, vec2(58.0), 3, 0.5, sd + 13.0);
  float c2 = tridge(uv, vec2(58.0), 3, 0.5, sd + 17.0);
  float cellNet = ss(0.55, 0.95, c1 * 0.6 + c2 * 0.6);
  h -= cellNet * 0.028;
  rough += cellNet * 0.09;

  /* stubble */
  if (stubble > 0.001){
    vec3 ws = tvor(uv, 190.0, sd + 21.0);
    float hair = ss(0.13, 0.02, ws.x) * step(0.35, ws.z);
    float region = ss(0.35, 0.6, tfbm2(uv, vec2(3.0), 3, 0.5, sd + 23.0) * 0.5 + 0.5);
    float k = hair * stubble * region;
    alb = mix(alb, uC[3], k * 0.8);
    rough += k * 0.22;
    h += k * 0.006;
  }

  /* blemishes, scars, freckles */
  if (blemish > 0.001){
    vec3 wb = tvor(uv, 30.0, sd + 31.0);
    float sp = ss(0.14, 0.03, wb.x) * step(0.72, wb.z) * blemish;
    alb = mix(alb, uC[1] * 0.62, sp * 0.7);
    float scar = ss(0.94, 0.99, tridge(uv, vec2(7.0), 3, 0.5, sd + 33.0)) * blemish;
    alb = mix(alb, uC[0] * 1.12, scar * 0.6);
    rough = mix(rough, 0.34, scar * 0.6);
    h -= scar * 0.010;
  }

  /* Sweat / sebum sheen. Skin's specular is never one lobe — the T-zone is oily, the
   * cheeks are matte, and inside the oily region the film breaks up at millimetre
   * scale. Two frequencies, so the highlight has structure instead of being a mirror
   * ball with one hot spot. */
  float oilZone = ss(0.35, 0.92, tfbm2(uv, vec2(5.0), 4, 0.5, sd + 41.0) * 0.5 + 0.5);
  float oilFine = tfbm2(uv, vec2(70.0), 3, 0.5, sd + 43.0) * 0.5 + 0.5;
  float o = oilZone * oil;
  rough = mix(rough, 0.20 + oilFine * 0.26, o);
  rough += (oilFine - 0.5) * 0.10;

  /* dirt and camo cream smudges */
  float grime = ss(0.6, 0.95, tturb(uv, vec2(5.0), 4, 0.5, sd + 51.0));
  alb = mix(alb, vec3(0.045, 0.045, 0.038), grime * 0.35);
  rough += grime * 0.10;

  /* ---- HUE GUARD: no green-family texel can leave this function ------------
   *
   * Every layer above is a multiply or a mix, and a stack of eight of them cannot be
   * reasoned about texel by texel — the iteration-3 build proved that by shipping
   * green-yellow patches on a jaw and a neck after each individual layer had been
   * checked in isolation. Human skin albedo is R > G > B everywhere on every human
   * being: melanin and haemoglobin both absorb short wavelengths monotonically. Assert
   * exactly that ordering and the hue is confined to 0-60 degrees by construction, so
   * "no green anywhere on skin" stops being a thing to re-measure after every edit.
   *
   * (The violet at the terminator that real skin does show is a SHADING effect and is
   * added in the SURF_SKIN block at runtime, downstream of this map. It is not, and
   * must not be, painted into the albedo.) */
  alb.g = min(alb.g, alb.r * 0.955);
  alb.b = min(alb.b, alb.g * 0.985);

  s.albedo = max(alb, vec3(0.006));
  s.rough  = clamp(rough, 0.12, 1.0);
  s.metal  = 0.0;
  s.ao     = sat(ao);
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,

/* ===================================================================== *
 * SHARED DETAIL / MICRO-SURFACE LAYER
 * Tiled at a high rate over everything so surfaces stay crisp up close.
 * ===================================================================== */
detail: /* glsl */`
void surface(vec2 uv, inout Surf s){
  float sd = uSeed;

  /* micro-grain — high frequency and low amplitude, so it never turns into lumps */
  float g1 = tfbm2(uv, vec2(200.0), 4, 0.5, sd + 3.0);
  float g2 = tturb(uv, vec2(430.0), 3, 0.5, sd + 5.0);
  vec3 wg = tvor(uv, 560.0, sd + 7.0);
  float grain = ss(0.5, 0.08, wg.x);

  /* fine scratches in several directions — this is what actually reads as "used" */
  float scr = 0.0;
  scr += ss(0.045, 0.0, tvorA(uv, vec2(260.0, 18.0), sd + 11.0).x);
  scr += ss(0.045, 0.0, tvorA(uv, vec2(20.0, 250.0), sd + 13.0).x) * 0.85;
  scr += ss(0.035, 0.0, tvorA(uv, vec2(170.0, 170.0), sd + 17.0).x) * 0.7;
  scr += ss(0.030, 0.0, tvorA(uv, vec2(160.0, 40.0), sd + 19.0).x) * 0.5;
  scr = sat(scr);

  float h = 0.5 + g1 * 0.16 + (g2 - 0.5) * 0.14 + grain * 0.10 - scr * 0.38;

  /* micro roughness breakup, mean 0.5 so it can be applied as a signed offset */
  float mr = 0.5 + g1 * 0.24 + (grain - 0.35) * 0.22 + scr * 0.38 + (g2 - 0.5) * 0.18;

  s.albedo = vec3(0.5);
  s.rough  = sat(mr);
  s.metal  = 0.0;
  s.ao     = 1.0;
  s.height = sat(h);
  s.alpha  = 1.0;
}
`,
};
